import { formatActivityBonusSummary } from './activity-bonuses';
import { trackGearEquipmentChanges, trackGearPurchase } from './analytics';
import { createClient } from '@supabase/supabase-js';
import { formatActivity } from './activities';
import { mountActivityForm, readActivityFormSubmission, renderActivityForm } from './activity-form';
import { WorkoutTimerWidget } from './workout-timer';
import type { Friend } from './friends';
import { type DailyClub, type ClubInvite, type CreatedClubInvite, type RedeemedInvite } from './clubs';
import { renderClubListItem, renderClubDetails } from './club-screen';
import { renderFriendsList, renderParticipantFriend } from './friends-screen';
import { EmailSignInClient } from './email-sign-in';
import { type UserProfile, type ProfileUpdate } from './profile';
import { mountUsernameForm, renderUsernameForm } from './username-form';
import { escapeSignInText, mountSignInForm } from './sign-in-form';
import { toggleGearItem, type PurchaseResult, type ShopData, type UserGear } from './gear';
import { renderGearContents, type GearPageState } from './gear-screen';
import { PurchaseRequestError, ShopPurchaseClient } from './shop-client';
import { renderRollDetails, renderShopContents, type ShopPageState } from './shop-screen';
import {
  createWorkoutAppHistoryState,
  getSecondaryView,
  shouldReturnToWorkoutOriginViaHistory,
  type AppHistoryState,
  type AppView,
  type PrimaryView,
  type SecondaryView,
  type UrlNavigationMode,
} from './app-history';
import {
  challengeConfig,
  challengeTypes,
  type ChallengeDetails,
  type ChallengeType,
} from './challenges';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, VAPID_PUBLIC_KEY } from './config';
import {
  resolveWorkoutRewards,
  TALENT_RESET_COST,
  type EarnedWorkoutRewards,
  type UserProgress,
} from './progression';
import {
  createPushSubscription,
  deactivatePushSubscriptionForSignOut,
  getExistingPushSubscription,
  getNotificationDeliveryState,
  getNotificationPermissionState,
  isNotificationDeliverySupported,
  registerNotificationServiceWorker,
  renderWorkoutNotificationControl,
  requestNotificationPermission,
  type NotificationDeliveryState,
  type PushSubscriptionLike,
  type ServiceWorkerRegistrationLike,
} from './notifications';
import {
  AUTH_CHECK_SLOW_MESSAGE,
  renderHeroArtwork,
  renderHeroTitle,
  renderPublicHome,
} from './public-home';
import {
  getInviteAuthRedirectUrl,
  renderInviteScreen,
} from './invite-screen';
import { renderHostInviteControls } from './invite-controls';
import {
  DEFAULT_INVITE_EXPIRATION,
  isInviteExpiration,
  type CreatedRoomInvite,
  type InviteExpiration,
  type InviteResolution,
  type Room,
  type RoomActivity,
  type RoomInvite,
} from './rooms';
import { clearLegacyLocalProgress } from './storage';
import {
  canAffordTalentLoadout,
  getTalentLoadoutCost,
  setTalentRankInLoadout,
  talentDefinitions,
  type ActivityType,
  type TalentDefinition,
  type TalentLoadout,
} from './talents';
import {
  getRecentWorkouts,
  getUserWorkoutPerformance,
  getWorkoutsNewestFirst,
} from './workout-history';

interface WorkoutRewardClaimResult {
  alreadyClaimed: boolean;
  progress: UserProgress;
  rewards: EarnedWorkoutRewards;
}

interface PendingActivitySubmission {
  roomId: string;
  type: ActivityType;
  qty: number;
  idempotencyKey: string;
}

const cardHeroImages = [
  '/public/assets/images/hero-girl-squat.png',
  '/public/assets/images/hero-man-burpee.png',
  '/public/assets/images/hero-man-pullup.png',
  '/public/assets/images/hero-man-lunge-red.png',
  '/public/assets/images/hero-woman-jumping-jack.png',
  '/public/assets/images/hero-woman-situp-2.png',
] as const;

const POLL_MS = 2 * 60 * 1000;
const AUTH_CHECK_SLOW_MS = 5000;
const PENDING_INVITE_STORAGE_KEY = 'passThePushups.pendingInvite';
const PENDING_INVITE_MAX_AGE_MS = Math.max(
  ...Object.values(challengeConfig).map((challenge) => challenge.durationMs),
);
const MOBILE_COLLAPSIBLE_MEDIA = window.matchMedia('(max-width: 919px)');

const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
const emailSignIn = new EmailSignInClient({
  auth: supabase.auth,
  storage: () => window.localStorage,
  now: Date.now,
});
let mountedSignInElement: HTMLElement | null = null;
let unmountSignIn: (() => void) | null = null;

let currentUser: { id: string; email?: string } | null = null;
let currentRoom: Room | null = null;
let currentClub: DailyClub | null = null;
let joinedClubs: DailyClub[] = [];
let clubsError = '';
let creationMode: 'workout' | 'club' = 'workout';
let clubNotice = '';
let clubClockOffset = 0;
let clubResetTimer: ReturnType<typeof setTimeout> | null = null;
let clubRequestVersion = 0;
let userProfile: UserProfile | null = null;
let profileSessionVersion = 0;
let usernameDialog: HTMLDialogElement | null = null;
let userProgress: UserProgress | null = null;
let talentSavePending = false;
let joinedRooms: Room[] = [];
let friends: Friend[] = [];
let friendsError = '';
let friendsRequestVersion = 0;
let pendingFriends = new Set<string>();
let roomInvites: Array<RoomInvite | ClubInvite> = [];
let latestCreatedInvite: CreatedRoomInvite | CreatedClubInvite | null = null;
let selectedInviteExpiration: InviteExpiration = DEFAULT_INVITE_EXPIRATION;
let selectedInviteFriendUserId = '';
let latestInviteFriendName = '';
let pendingActivitySubmission: PendingActivitySubmission | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let rewardModalShown = false;
let currentView: AppView = 'lobby';
let workoutTimer: { userId: string; roomId: string; widget: WorkoutTimerWidget } | null = null;
let gearPage: GearPageState | null = null;
let shopPage: ShopPageState | null = null;
let shopPurchases: ShopPurchaseClient | null = null;
let pageRenderVersion = 0;
let workoutPageOrigin: PrimaryView = 'lobby';
let notificationDeliveryState: NotificationDeliveryState = 'unsupported';
let notificationServiceWorker: Promise<ServiceWorkerRegistrationLike | null> | null = null;
let currentPushSubscription: PushSubscriptionLike | null = null;
let notificationEnrollmentRequest: Promise<void> | null = null;
const cardArtworkByContext = new Map<string, readonly [string, string]>();

function formatTalentPointValue(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }

  return value.toFixed(2).replace(/\.?0+$/, '');
}

function formatTalentCost(talent: TalentDefinition): string {
  return talent.maxPoints > 1 ? `Cost ${talent.cost} each` : `Cost ${talent.cost}`;
}

function canSetTalentRank(talentId: string, rank: number, availableTalentPoints: number): boolean {
  const currentLoadout = userProgress?.talentLoadout ?? {};
  if (rank <= (currentLoadout[talentId] ?? 0)) return false;
  const nextLoadout = setTalentRankInLoadout(currentLoadout, talentId, rank);
  return canAffordTalentLoadout(nextLoadout, availableTalentPoints);
}

async function saveTalentRank(talentId: string, rank: number): Promise<boolean> {
  if (!userProgress || talentSavePending || rank <= (userProgress.talentLoadout[talentId] ?? 0)) return false;

  const nextLoadout = setTalentRankInLoadout(userProgress.talentLoadout, talentId, rank);
  if (!canAffordTalentLoadout(nextLoadout, userProgress.talentPoints)) {
    alert('Not enough available talent points for that talent configuration.');
    return false;
  }

  const userId = currentUser?.id;
  talentSavePending = true;
  try {
    const progress = await apiSaveTalentLoadout(nextLoadout);
    if (currentUser?.id !== userId) return false;
    userProgress = progress;
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Could not save talent loadout.';
    alert(message);
    return false;
  } finally {
    talentSavePending = false;
  }
}

function getRoomIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('roomId');
}

function getClubIdFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('clubId');
}

function setClubIdInUrl(clubId: string, mode: UrlNavigationMode = 'replace'): void {
  const url = new URL(window.location.href);
  url.searchParams.set('clubId', clubId);
  url.searchParams.delete('roomId');
  url.searchParams.delete('invite');
  url.searchParams.delete('view');
  updateBrowserUrl(url, mode, createWorkoutAppHistoryState('lobby', mode));
}

function getPrimaryViewFromUrl(): PrimaryView {
  const params = new URLSearchParams(window.location.search);
  return params.get('view') === 'history' ? 'history' : 'lobby';
}

function getInviteTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('invite');
  if (urlToken) {
    try {
      localStorage.setItem(PENDING_INVITE_STORAGE_KEY, JSON.stringify({
        token: urlToken,
        expiresAt: Date.now() + PENDING_INVITE_MAX_AGE_MS,
      }));
    } catch {
      // The URL remains the fallback when storage is unavailable.
    }
    return urlToken;
  }

  try {
    const stored = JSON.parse(localStorage.getItem(PENDING_INVITE_STORAGE_KEY) ?? 'null') as {
      token?: unknown;
      expiresAt?: unknown;
    } | null;
    if (stored && typeof stored.token === 'string' && Number(stored.expiresAt) > Date.now()) {
      return stored.token;
    }
    localStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
  } catch {
    // Continue without stored invite state.
  }
  return null;
}

function updateBrowserUrl(
  url: URL,
  mode: UrlNavigationMode,
  state: AppHistoryState = {},
): void {
  const method = mode === 'push' ? 'pushState' : 'replaceState';
  window.history[method](state, '', url.toString());
}

function setPrimaryViewInUrl(
  view: PrimaryView,
  mode: UrlNavigationMode = 'push',
): void {
  const url = new URL(window.location.href);
  if (view === 'history') {
    url.searchParams.set('view', 'history');
  } else {
    url.searchParams.delete('view');
  }
  url.searchParams.delete('roomId');
  url.searchParams.delete('invite');
  url.searchParams.delete('clubId');
  updateBrowserUrl(url, mode, { appView: view });
}

function setRoomIdInUrl(
  roomId: string,
  origin: PrimaryView = 'lobby',
  mode: UrlNavigationMode = 'replace',
): void {
  const url = new URL(window.location.href);
  url.searchParams.set('roomId', roomId);
  url.searchParams.delete('clubId');
  url.searchParams.delete('invite');
  if (origin === 'history') {
    url.searchParams.set('view', 'history');
  } else {
    url.searchParams.delete('view');
  }
  updateBrowserUrl(url, mode, createWorkoutAppHistoryState(origin, mode));
}

function clearRoomIdInUrl(mode: UrlNavigationMode = 'replace'): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('roomId');
  updateBrowserUrl(url, mode, { appView: getPrimaryViewFromUrl() });
}

function clearAppNavigationFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('roomId');
  url.searchParams.delete('invite');
  url.searchParams.delete('view');
  url.searchParams.delete('clubId');
  updateBrowserUrl(url, 'replace', { appView: 'lobby' });
}

function clearInviteTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('invite');
  window.history.replaceState({}, '', url.toString());
  try {
    localStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
  } catch {
    // The URL has still been cleared.
  }
}

function getPrimaryViewHref(view: PrimaryView): string {
  return view === 'history' ? '/?view=history' : '/';
}

function getRoomHref(roomId: string, origin: PrimaryView): string {
  const params = new URLSearchParams();
  if (origin === 'history') params.set('view', 'history');
  params.set('roomId', roomId);
  return `/?${params.toString().replace(/&/g, '&amp;')}`;
}

function shouldHandleNavigationClick(event: MouseEvent): boolean {
  return event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;
}

function canReturnToWorkoutOriginWithBrowserHistory(): boolean {
  return shouldReturnToWorkoutOriginViaHistory(
    window.history.state,
    workoutPageOrigin,
  );
}

function formatActivityTimestamp(timestamp: number): string {
  const submittedAt = new Date(timestamp);
  if (Number.isNaN(submittedAt.getTime())) {
    return 'Unknown time';
  }

  return submittedAt.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getCurrentUserPoints(): number {
  if (!currentRoom || !currentUser) return 0;
  return currentRoom.participantPoints[currentUser.id] ?? 0;
}

function formatRemaining(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

function getRoomTimeStatus(room: Room): string {
  if (room.status === 'completed') return 'Completed';
  if (room.status === 'expired') return 'Expired';
  const remainingMs = room.expiresAt - Date.now();
  return `Ends in ${formatRemaining(remainingMs)}`;
}

function formatWorkoutDate(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRepCount(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function renderWorkoutStatus(room: Room): string {
  const label = room.status === 'completed'
    ? 'Completed'
    : room.status === 'expired'
      ? 'Expired'
      : 'Active';
  const icon = room.status === 'completed'
    ? '<img class="saved-room-completed-icon" src="/public/assets/images/icon-green-check.png" alt="" aria-hidden="true" width="24" height="24" />'
    : '';

  return `
    <span class="workout-status workout-status-${room.status}">
      ${icon}
      <span class="workout-status-badge">${label}</span>
    </span>
  `;
}

function renderSavedRoomItem(room: Room, origin: PrimaryView): string {
  const playerCount = room.participants.length;
  const remaining = Math.max(0, room.requiredPoints - room.currentPoints);
  const activeTiming = room.status === 'active' ? ` · ${getRoomTimeStatus(room)}` : '';
  const actionLabel = room.status === 'active' ? 'Continue' : 'View';

  return `
    <li class="saved-room-item saved-room-item-${room.status}">
      <div class="saved-room-heading">
        <p class="saved-room-title">${room.challengeSnapshot.title}</p>
        ${renderWorkoutStatus(room)}
      </div>
      <p class="saved-room-sub">${room.currentPoints}/${room.requiredPoints} team pts · ${playerCount} players · ${remaining} points left${activeTiming}</p>
      <div class="saved-room-actions">
        <a
          class="btn saved-room-open${room.status === 'active' ? '' : ' saved-room-view'}"
          href="${getRoomHref(room.id, origin)}"
          data-room-id="${room.id}"
        >${actionLabel}</a>
      </div>
    </li>
  `;
}

function renderWorkoutHistoryItem(room: Room): string {
  const userId = currentUser?.id ?? '';
  const performance = getUserWorkoutPerformance(room, userId);
  const activityBreakdown = performance.activityTotals.length
    ? `
      <ul class="workout-activity-breakdown">
        ${performance.activityTotals.map((activity) => `
          <li>${formatActivity(activity.type, activity.qty)}</li>
        `).join('')}
      </ul>
    `
    : '<p class="workout-no-activity">No activity logged</p>';
  const actionLabel = room.status === 'active' ? 'Continue' : 'View';

  return `
    <li class="workout-history-item workout-history-item-${room.status}">
      <div class="workout-history-heading">
        <div>
          <p class="workout-history-date">${room.clubDay ? escapeSignInText(room.clubDay) : formatWorkoutDate(room.createdAt)}</p>
          <h3>${room.challengeSnapshot.title}</h3>
        </div>
        ${renderWorkoutStatus(room)}
      </div>

      <div class="workout-performance-grid">
        <div class="workout-performance-stat">
          <span>Your points</span>
          <strong>${performance.points}</strong>
        </div>
        <div class="workout-performance-stat">
          <span>Total reps</span>
          <strong>${formatRepCount(performance.totalReps)}</strong>
        </div>
        ${performance.totalSeconds > 0 ? `
          <div class="workout-performance-stat">
            <span>Total time</span>
            <strong>${formatRepCount(performance.totalSeconds)}s</strong>
          </div>
        ` : ''}
        <div class="workout-performance-stat">
          <span>Team points</span>
          <strong>${room.currentPoints} / ${room.requiredPoints}</strong>
        </div>
        <div class="workout-performance-stat">
          <span>Players</span>
          <strong>${room.participants.length}</strong>
        </div>
      </div>

      <div class="workout-activity-summary">
        <p class="workout-activity-label">Your activity</p>
        ${activityBreakdown}
      </div>

      <a
        class="btn saved-room-open workout-history-open${room.status === 'active' ? '' : ' saved-room-view'}"
        href="${getRoomHref(room.id, 'history')}"
        data-room-id="${room.id}"
      >${actionLabel}</a>
    </li>
  `;
}

function getChallengeDetails(challengeType: ChallengeType) {
  return challengeConfig[challengeType];
}

function getRewardItemMarkup(challenge: ChallengeDetails): string {
  if (challenge.rewardMode === 'gold-only') {
    return `
      <span class="goal-reward-item">
        <img class="goal-reward-icon" src="/public/assets/images/icon-gold-coin.png" alt="" aria-hidden="true" width="20" height="20" />
        +${challenge.goldReward} Gold
      </span>
    `;
  }

  if (challenge.rewardMode === 'talent-only') {
    return `
      <span class="goal-reward-item">
        <img class="goal-reward-icon" src="/public/assets/images/icon-talent-flexed-arm-gold.png" alt="" aria-hidden="true" width="20" height="20" />
        +${challenge.talentReward} Talent
      </span>
    `;
  }

  return `
    <span class="goal-reward-item">
      <img class="goal-reward-icon" src="/public/assets/images/icon-talent-flexed-arm-gold.png" alt="" aria-hidden="true" width="20" height="20" />
      +${challenge.talentReward} Talent
    </span>
    <span class="goal-reward-item">
      <img class="goal-reward-icon" src="/public/assets/images/icon-gold-coin.png" alt="" aria-hidden="true" width="20" height="20" />
      +${challenge.goldReward} Gold${challenge.rewardMode === 'conditional' ? ' (max level)' : ''}
    </span>
  `;
}

function resolveEarnedRewards(challenge: ChallengeDetails, progress: UserProgress | null): EarnedWorkoutRewards {
  return resolveWorkoutRewards(progress ?? { level: 0, talentPoints: 0 }, challenge);
}

function renderActivityLog(): string {
  if (!currentRoom || currentRoom.activities.length === 0) {
    return '<li class="feed-item">No activities logged</li>';
  }

  return currentRoom.activities
    .slice()
    .reverse()
    .slice(0, 14)
    .map((item) => {
      const byMe = currentUser?.id === item.userId;
      const actor = byMe ? 'You' : escapeSignInText(item.username);
      return `
      <li class="feed-item">
        <div class="feed-copy">
          <span class="feed-main">${actor} logged <strong>${formatActivity(item.type, item.qty)}</strong></span>
          <div class="feed-meta">
            <span class="feed-time">Submitted ${formatActivityTimestamp(item.timestamp)}</span>
            <span class="feed-bonus">${formatActivityBonusSummary(item)}</span>
          </div>
        </div>
        <span class="feed-points">+${item.points} pts</span>
      </li>
    `;
    })
    .join('');
}

function renderParticipantBreakdown(): string {
  if (!currentRoom) return '';

  const room = currentRoom;

  return room.participants
    .map((participant) => {
      const points = room.participantPoints[participant.userId] ?? 0;
      const isMe = participant.userId === currentUser?.id;
      return `
      <div class="score-block">
        <div class="participant-heading">
          <p>${escapeSignInText(participant.username)}${isMe ? ' (You)' : ''}</p>
          ${isMe ? '' : `<span data-friend-control="${participant.userId}">${renderParticipantFriend(participant, friends.some((friend) => friend.userId === participant.userId), pendingFriends.has(participant.userId))}</span>`}
        </div>
        <strong>${points}</strong>
      </div>
    `;
    })
    .join('');
}

function renderHeroHud(options: { roomId?: string; enableTalentLink?: boolean; enableGearLink?: boolean; enableShopLink?: boolean } = {}): string {
  const { roomId, enableTalentLink = true, enableGearLink = true, enableShopLink = true } = options;
  const level = userProgress?.level ?? 0;
  const talentPoints = userProgress?.talentPoints ?? 0;
  const gold = userProgress?.gold ?? 0;
  const talentMarkup = enableTalentLink
    ? `
      <button id="hero-talent-btn" class="hero-hud-item hero-hud-button" type="button">
        <span class="hero-hud-label">Talent</span>
        <img
          class="hero-hud-progress-icon"
          src="/public/assets/images/icon-talent-flexed-arm-gold.png"
          alt=""
          aria-hidden="true"
          width="16"
          height="16"
        />
        <strong class="hero-hud-value">${talentPoints}</strong>
      </button>
    `
    : `
      <span class="hero-hud-item hero-hud-item-active" aria-current="page">
        <span class="hero-hud-label">Talent</span>
        <img
          class="hero-hud-progress-icon"
          src="/public/assets/images/icon-talent-flexed-arm-gold.png"
          alt=""
          aria-hidden="true"
          width="16"
          height="16"
        />
        <strong class="hero-hud-value">${talentPoints}</strong>
      </span>
    `;

  return `
    <div class="hero-hud" aria-label="Player progress">
      ${enableGearLink
        ? '<button id="hero-level-btn" class="hero-hud-item hero-hud-button" type="button" aria-label="View and equip gear">'
        : '<span class="hero-hud-item hero-hud-item-active" aria-current="page">'}
        <span class="hero-hud-label">Lvl</span>
        <img
          class="hero-hud-progress-icon"
          src="/public/assets/images/icon-level-up-chevron.png"
          alt=""
          aria-hidden="true"
          width="16"
          height="16"
        />
        <strong class="hero-hud-value">${level}</strong>
      ${enableGearLink ? '</button>' : '</span>'}
      ${talentMarkup}
      ${enableShopLink
        ? '<button id="hero-gold-btn" class="hero-hud-item hero-hud-button hero-hud-secondary" type="button" aria-label="Open gear shop">'
        : '<span class="hero-hud-item hero-hud-item-active" aria-current="page">'}
        <span class="hero-hud-label">Gold</span>
        <img
          class="hero-hud-progress-icon"
          src="/public/assets/images/icon-gold-coin.png"
          alt=""
          aria-hidden="true"
          width="16"
          height="16"
        />
        <strong class="hero-hud-value" data-gold-balance>${gold}</strong>
      ${enableShopLink ? '</button>' : '</span>'}
      ${roomId ? `<span class="hero-hud-item hero-hud-room">Workout ${roomId}</span>` : ''}
    </div>
  `;
}

function getCardArtwork(contextKey: string): readonly [string, string] {
  const existingArtwork = cardArtworkByContext.get(contextKey);
  if (existingArtwork) {
    return existingArtwork;
  }

  const shuffledArtwork = [...cardHeroImages];
  for (let index = shuffledArtwork.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffledArtwork[index], shuffledArtwork[randomIndex]] = [
      shuffledArtwork[randomIndex],
      shuffledArtwork[index],
    ];
  }

  const selectedArtwork = [shuffledArtwork[0], shuffledArtwork[1]] as const;
  cardArtworkByContext.set(contextKey, selectedArtwork);
  return selectedArtwork;
}

function renderLobbyScreen(): string {
  const recentRooms = getRecentWorkouts(joinedRooms.filter((room) => !room.clubId));
  const [startOrJoinArtwork, savedRoomsArtwork] = getCardArtwork('lobby');
  const challengeCardsMarkup = challengeTypes
    .map((challengeType) => {
      const challenge = getChallengeDetails(challengeType);
      return `
        <div class="goal-display">
          <div class="goal-heading">
            <h3 class="goal-title" aria-label="${challenge.goalPointsPerUser} Points in ${challenge.durationLabel}">
              <span class="goal-points-value">${challenge.goalPointsPerUser}</span>
              <span class="goal-points-unit">Points</span>
            </h3>
            <span
              class="goal-duration-badge"
              data-duration-hours="${creationMode === 'club' ? 24 : challenge.durationMs / (60 * 60 * 1000)}"
              aria-hidden="true"
            >
              <img
                class="goal-duration-icon"
                src="/public/assets/images/icon-stopwatch-white-face.png"
                alt=""
                width="18"
                height="18"
              />
              <span>${creationMode === 'club' ? 'Until midnight' : challenge.durationLabel}</span>
            </span>
          </div>
          <div class="goal-rewards" aria-label="Rewards">
            <p class="goal-rewards-label">Rewards</p>
            <div class="goal-rewards-list">
              ${getRewardItemMarkup(challenge)}
            </div>
          </div>
          <button class="btn btn-large create-room-btn" data-challenge-type="${challengeType}">${creationMode === 'club' ? 'Create Club' : challenge.ctaLabel}</button>
        </div>
      `;
    })
    .join('');

  const savedRoomsMarkup = recentRooms.length
    ? recentRooms.map((room) => renderSavedRoomItem(room, 'lobby')).join('')
    : joinedClubs.length ? '' : '<li class="saved-room-empty">No workouts in the last 7 days.</li>';

  return `
    <main class="screen">
      <header class="hero card">
        <div class="hero-content hero-content-lobby">
          <div class="hero-brand">
            <p class="eyebrow">Workout-Based Multiplayer</p>
            ${renderHeroTitle()}
          </div>
          <div class="hero-account">
            <p class="hero-account-copy">Logged in as ${escapeSignInText(currentUser?.email || '')}</p>
            <p class="hero-username"><strong>${escapeSignInText(userProfile?.username ?? '')}</strong> <button id="edit-username-btn" class="username-edit" type="button" aria-label="Edit username">Edit</button></p>
            <button id="logout-btn" class="btn-logout">Sign Out</button>
          </div>
          ${renderHeroHud()}
          ${renderHeroArtwork()}
        </div>
      </header>

      <section class="layout-grid lobby-layout-grid">
        <article class="card welcome-card mobile-collapsible-card" data-collapsible-card>
          <h2 class="collapsible-card-heading">
            <button
              class="collapsible-card-toggle"
              type="button"
              aria-expanded="true"
              aria-controls="start-or-join-content"
            >
              <span class="collapsible-card-title">Start Or Join</span>
              <span class="collapsible-card-chevron" aria-hidden="true"></span>
            </button>
          </h2>
          <div id="start-or-join-content" class="collapsible-card-content welcome-card-content">
            <img
              class="section-card-artwork lobby-card-artwork"
              src="${startOrJoinArtwork}"
              alt=""
              aria-hidden="true"
              width="1536"
              height="1024"
            />
            <p class="card-copy lobby-card-copy">Daily challenges are available every day. Starting one creates your own workout for that challenge.</p>

            <label class="creation-mode-label" for="creation-mode">Start a</label>
            <select id="creation-mode"><option value="workout"${creationMode === 'workout' ? ' selected' : ''}>Workout</option><option value="club"${creationMode === 'club' ? ' selected' : ''}>Daily Club</option></select>
            ${creationMode === 'club' ? `<p class="card-copy">Repeats each day with the same members and rewards. Resets at midnight (${escapeSignInText(Intl.DateTimeFormat().resolvedOptions().timeZone)}).</p>` : ''}

            ${challengeCardsMarkup}
          </div>
        </article>

        <div class="lobby-personal-column">
        <article class="card saved-rooms-card mobile-collapsible-card" data-collapsible-card>
          <div class="saved-rooms-card-heading">
            <h2 class="collapsible-card-heading">
              <button
                class="collapsible-card-toggle"
                type="button"
                aria-expanded="true"
                aria-controls="your-workouts-content"
              >
                <span class="collapsible-card-title">Your Workouts</span>
                <span class="collapsible-card-chevron" aria-hidden="true"></span>
              </button>
            </h2>
            <a id="workout-history-link" class="workout-history-link" href="${getPrimaryViewHref('history')}">View Workout History</a>
          </div>
          <div id="your-workouts-content" class="collapsible-card-content saved-rooms-card-content">
            <img
              class="section-card-artwork lobby-card-artwork"
              src="${savedRoomsArtwork}"
              alt=""
              aria-hidden="true"
              width="1536"
              height="1024"
            />
            <p class="card-copy lobby-card-copy">Jump back into an active workout or review a recent result.</p>
            ${clubsError ? `<p class="card-copy" role="status">${escapeSignInText(clubsError)}</p>` : ''}
            <ul class="saved-room-list">${joinedClubs.map(renderClubListItem).join('')}${savedRoomsMarkup}</ul>
          </div>
        </article>
        <article class="card friends-card" aria-labelledby="friends-title">
          <h2 id="friends-title">Your Friends</h2>
          <div data-friends-list>${renderFriendsList(friends, friendsError)}</div>
        </article>
        </div>
      </section>
    </main>
  `;
}

function renderWorkoutHistoryScreen(): string {
  const historyRooms = getWorkoutsNewestFirst(joinedRooms);
  const historyMarkup = historyRooms.length
    ? historyRooms.map(renderWorkoutHistoryItem).join('')
    : '<li class="saved-room-empty">You have not joined any workouts yet.</li>';

  return `
    <main class="screen">
      <header class="hero card workout-hero history-hero">
        <div class="hero-content hero-content-workout hero-content-history-page">
          <div class="workout-hero-brand history-hero-brand">
            <a id="history-back-btn" class="workout-back-link" href="${getPrimaryViewHref('lobby')}">
              <span aria-hidden="true">←</span>
              Back to Home
            </a>
            ${renderHeroTitle()}
          </div>
          <div class="workout-hero-summary history-hero-summary">
            <p class="workout-hero-challenge">Workout History</p>
          </div>
          ${renderHeroHud()}
          ${renderHeroArtwork()}
          <button id="logout-btn" class="btn-logout" type="button">Sign Out</button>
        </div>
      </header>

      <section class="layout-grid workout-history-layout">
        <article class="card workout-history-card">
          <div>
            <p class="eyebrow">Your Performance</p>
            <h2>Workout History</h2>
          </div>
          <p class="card-copy">See how you showed up for your team in every workout.</p>
          <ul class="workout-history-list">${historyMarkup}</ul>
        </article>
      </section>
    </main>
  `;
}

function renderTalentTreeScreen(): string {
  const totalTalentPoints = userProgress?.talentPoints ?? 0;
  const talentLoadout = userProgress?.talentLoadout ?? {};
  const spentTalentPoints = getTalentLoadoutCost(talentLoadout);
  const availableTalentPoints = Math.max(0, totalTalentPoints - spentTalentPoints);

  const talentCardsMarkup = talentDefinitions
    .map((talent) => {
      const rank = talentLoadout[talent.id] ?? 0;
      const nextNodeRank = rank + 1;
      const nodeUnavailable = rank >= talent.maxPoints || !canSetTalentRank(talent.id, nextNodeRank, totalTalentPoints);
      const rankPips = Array.from({ length: talent.maxPoints }, (_, index) => {
        const pipValue = index + 1;
        const filled = pipValue <= rank;
        const nextPipRank = pipValue;
        const pipUnavailable = !canSetTalentRank(talent.id, nextPipRank, totalTalentPoints);
        return `<button type="button" class="talent-pip talent-pip-button${filled ? ' talent-pip-filled' : ''}" data-talent-id="${talent.id}" data-pip-value="${pipValue}" aria-label="Set ${talent.title} to rank ${pipValue}" aria-pressed="${filled}" ${pipUnavailable ? 'disabled' : ''}></button>`;
      }).join('');

      return `
        <div class="talent-node talent-node-interactive${talent.highlight ? ' talent-node-featured' : ''}${rank > 0 ? ' talent-node-active' : ''}${nodeUnavailable ? ' talent-node-unavailable' : ''}" data-talent-id="${talent.id}" data-max-points="${talent.maxPoints}" role="button" tabindex="0" aria-disabled="${nodeUnavailable}" aria-label="${talent.title} rank ${rank} of ${talent.maxPoints}. Click to increment, or use pips to set a specific rank.">
          <div class="talent-node-topline">
            <div>
              <p class="talent-node-kicker">Talent</p>
              <h3>${talent.title}</h3>
            </div>
            <span class="talent-cost-pill">${formatTalentCost(talent)}</span>
          </div>

          <p class="card-copy">${talent.description}</p>

          <div class="talent-rank-row">
            <div class="talent-pip-row" aria-label="Max rank ${talent.maxPoints}">${rankPips}</div>
            <span class="talent-rank-label">Max ${talent.maxPoints}</span>
          </div>

          <span class="talent-node-status">${talent.effectLabel}</span>
        </div>
      `;
    })
    .join('');

  return `
    <main class="screen">
      <header class="hero card workout-hero talent-hero">
        <div class="hero-content hero-content-workout hero-content-talent">
          <div class="workout-hero-brand talent-hero-brand">
            <button id="talent-back-btn" class="workout-back-link" type="button">
              <span aria-hidden="true">←</span>
              Back to Home
            </button>
            ${renderHeroTitle()}
          </div>
          <div class="workout-hero-summary talent-hero-summary">
            <p class="workout-hero-challenge">Player Progression</p>
          </div>
          ${renderHeroHud({ enableTalentLink: false })}
          ${renderHeroArtwork()}
          <button id="logout-btn" class="btn-logout" type="button">Sign Out</button>
        </div>
      </header>

      <section class="layout-grid talent-layout-grid">
        <article class="card talent-overview-card">
          <div class="talent-points-callout">
            <p class="talent-points-label">Available Talent Points</p>
            <strong class="talent-points-value">${formatTalentPointValue(availableTalentPoints)}</strong>
            <p class="talent-points-detail">${formatTalentPointValue(spentTalentPoints)} spent of ${formatTalentPointValue(totalTalentPoints)}</p>
            <button id="talent-reset-btn" class="btn-secondary" type="button" ${spentTalentPoints === 0 || (userProgress?.gold ?? 0) < TALENT_RESET_COST ? 'disabled' : ''}>Reset tree — ${TALENT_RESET_COST} gold</button>
            <p class="card-copy">Reset to refund all spent talent points. Existing workouts keep their bonuses.</p>
            ${spentTalentPoints > 0 && (userProgress?.gold ?? 0) < TALENT_RESET_COST ? `<p class="card-copy">You need ${TALENT_RESET_COST} gold to reset.</p>` : ''}
          </div>
        </article>

        <article class="card talent-path-card">
          <p class="eyebrow">Talent Tree</p>
          <h2>Activity Bonuses</h2>

          <div class="talent-path-grid">${talentCardsMarkup}</div>
        </article>
      </section>
    </main>
  `;
}

function renderPendingClubScreen(club: DailyClub): string {
  return `<main class="screen workout-screen">
    <header class="hero card workout-hero"><div class="hero-content hero-content-workout">
      <div class="workout-hero-brand"><button id="back-to-home-btn" class="workout-back-link" type="button">← Back to Home</button>${renderHeroTitle()}</div>
      <div class="workout-hero-summary"><h1 class="workout-hero-challenge">${escapeSignInText(club.challengeSnapshot.title)}</h1></div>
      ${renderHeroHud()}${renderHeroArtwork()}<button id="logout-btn" class="btn-logout" type="button">Sign Out</button>
    </div></header>
    <article class="card club-waiting"><h2>You’re in! Start tomorrow.</h2>
      <p class="card-copy">Today’s workout is already complete. Your first workout starts at the next midnight.</p>
      ${renderClubDetails(club, clubNotice)}
      <h3>Club members</h3><ul class="friends-list">${club.members.map((member) => `<li>${escapeSignInText(member.username)}</li>`).join('')}</ul>
    </article></main>`;
}

function renderWorkoutScreen(): string {
  if (!currentRoom) return '';

  const progressPercent = currentRoom.requiredPoints
    ? Math.min(100, (currentRoom.currentPoints / currentRoom.requiredPoints) * 100)
    : 0;
  const remaining = Math.max(0, currentRoom.requiredPoints - currentRoom.currentPoints);
  const myPoints = getCurrentUserPoints();
  const isClosed = currentRoom.status !== 'active';
  const roomStatusText = getRoomTimeStatus(currentRoom);
  const challenge = currentRoom.challengeSnapshot;
  const [activityArtwork, progressArtwork] = getCardArtwork(`workout:${currentRoom.id}`);
  const backLabel = workoutPageOrigin === 'history' ? 'Back to History' : 'Back to Home';
  const playerLabel = `${currentRoom.participants.length} ${currentRoom.participants.length === 1 ? 'Player' : 'Players'}`;
  const activityIntroMarkup = isClosed
    ? `
      <h2>Activity History</h2>
      ${currentRoom.status === 'expired' ? `<p class="card-copy">${challenge.expiredLabel}</p>` : ''}
    `
    : `
      <h2>Log Activity</h2>
      <p class="card-copy">Log your activity and help your team reach the goal.</p>
      <p class="card-copy">Goal: ${challenge.goalPointsPerUser} points per person.</p>
    `;
  const activityFormMarkup = isClosed
    ? ''
    : renderActivityForm();

  return `
    <main class="screen workout-screen">
      <header class="hero card workout-hero">
        <div class="hero-content hero-content-workout">
          <div class="workout-hero-brand">
            <button id="back-to-home-btn" class="workout-back-link" type="button">
              <span aria-hidden="true">←</span>
              ${backLabel}
            </button>
            ${renderHeroTitle()}
          </div>
          <div class="workout-hero-summary">
            <p class="workout-hero-challenge">${challenge.title}</p>
            <span class="workout-hero-status workout-hero-status-${currentRoom.status}">${roomStatusText}</span>
            <div class="goal-rewards" aria-label="Rewards">
              <p class="goal-rewards-label">Rewards</p>
              <div class="goal-rewards-list">${getRewardItemMarkup(challenge)}</div>
            </div>
          </div>
          ${renderHeroHud()}
          ${renderHeroArtwork()}
          <button id="logout-btn" class="btn-logout" type="button">Sign Out</button>
        </div>
      </header>

      <section class="layout-grid workout-layout-grid">
        <article class="card activity-card workout-activity-card">
          <img
            class="section-card-artwork workout-card-artwork"
            src="${activityArtwork}"
            alt=""
            aria-hidden="true"
            width="1536"
            height="1024"
          />
          <div class="workout-activity-intro">
            ${activityIntroMarkup}
          </div>
          ${currentClub ? renderClubDetails(currentClub, clubNotice) : currentRoom.clubId ? `<p class="card-copy">Club workout for ${escapeSignInText(currentRoom.clubDay ?? '')}.</p>` : ''}
          <div data-workout-timer></div>
          ${activityFormMarkup}
          <ul class="feed">${renderActivityLog()}</ul>
        </article>

        <article class="card workout-card">
          <img
            class="section-card-artwork workout-card-artwork"
            src="${progressArtwork}"
            alt=""
            aria-hidden="true"
            width="1536"
            height="1024"
          />
          <div class="workout-progress-intro">
            <div class="workout-header">
              <div class="workout-progress-heading">
                <h2>Workout Progress</h2>
                <span class="round-badge">${playerLabel}</span>
                <p class="workout-progress-points">${currentRoom.currentPoints} / ${currentRoom.requiredPoints} points</p>
              </div>
            </div>

            <p class="status">${remaining} points remaining · ${isClosed ? `You scored ${myPoints} points` : `You have ${myPoints} points`}</p>
          </div>

          <div class="progress-section">
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${progressPercent}%"></div>
            </div>
          </div>

          <section>
            <h3>Participant Breakdown</h3>
            <div class="scoreboard">${renderParticipantBreakdown()}</div>
            <p class="card-copy" id="friend-action-status" role="status"></p>
          </section>

          ${renderHostInviteControls({
            room: currentClub ? { createdBy: currentClub.hostUserId, expiresAt: null, status: 'active' }
              : currentRoom.clubId ? { ...currentRoom, status: 'expired' } : currentRoom,
            currentUserId: currentUser?.id ?? null,
            invites: roomInvites,
            latestInvite: latestCreatedInvite,
            selectedExpiration: selectedInviteExpiration,
            origin: window.location.origin,
            friends: friends.filter((friend) => !(currentClub?.members ?? currentRoom!.participants).some((person) => person.userId === friend.userId)),
            selectedFriendUserId: selectedInviteFriendUserId,
            latestInviteFriendName,
          })}

          ${renderWorkoutNotificationControl(notificationDeliveryState)}

          <div class="stats-bar">
            <div class="stat">
              <p class="stat-label">Talent Points</p>
              <strong>${userProgress?.talentPoints ?? 0}</strong>
            </div>
            <div class="stat">
              <p class="stat-label">Gold</p>
              <strong>${userProgress?.gold ?? 0}</strong>
            </div>
            <div class="stat">
              <p class="stat-label">Level</p>
              <strong>${userProgress?.level ?? 0}</strong>
            </div>
          </div>
        </article>
      </section>
    </main>
  `;
}

async function openSecondaryPage(view: SecondaryView): Promise<void> {
  if (currentView === view) return;
  resetCurrentRoomState();
  gearPage = null;
  currentView = view;
  const url = new URL(window.location.href);
  url.searchParams.set('view', view === 'talent-tree' ? 'talents' : view);
  url.searchParams.delete('roomId');
  url.searchParams.delete('clubId');
  url.searchParams.delete('invite');
  updateBrowserUrl(url, 'push', { appView: view });
  window.scrollTo(0, 0);
  await renderPage();
}

function attachTalentHudHandler(root: HTMLElement): void {
  const talentBtn = root.querySelector<HTMLButtonElement>('#hero-talent-btn');
  if (!talentBtn) return;

  talentBtn.addEventListener('click', () => void openSecondaryPage('talent-tree'));
}

function renderGearScreen(view: 'gear' | 'shop' = 'gear'): string {
  return `
    <main class="screen gear-screen" data-screen="${view}">
      <header class="hero card workout-hero">
        <div class="hero-content hero-content-workout">
          <div class="workout-hero-brand">
            <button id="${view}-back-btn" class="workout-back-link" type="button"><span aria-hidden="true">←</span> Back to Home</button>
            ${renderHeroTitle()}
          </div>
          <div class="workout-hero-summary"><h1 class="workout-hero-challenge">${view === 'gear' ? 'Gear' : 'Gear Shop'}</h1></div>
          ${renderHeroHud({ enableGearLink: view !== 'gear', enableShopLink: view !== 'shop' })}
          ${renderHeroArtwork()}
          <button id="logout-btn" class="btn-logout" type="button">Sign Out</button>
        </div>
      </header>
      <div id="${view}-contents" class="gear-sections"></div>
      ${view === 'shop' ? '<dialog id="shop-roll-dialog" class="shop-dialog" aria-labelledby="shop-roll-title"></dialog>' : ''}
    </main>`;
}

function updateGearContents(page: GearPageState, focusId?: string): void {
  if (gearPage !== page || currentUser?.id !== page.userId || currentView !== 'gear') return;
  const container = document.getElementById('gear-contents');
  if (!container) return;
  container.innerHTML = renderGearContents(page);
  container.querySelector('#gear-retry-btn')?.addEventListener('click', () => void loadGearPage(page));
  container.querySelectorAll<HTMLButtonElement>('[data-gear-item]').forEach((button) => {
    button.addEventListener('click', () => void saveGearItem(page, button.dataset.gearItem!, button.id));
  });
  if (focusId) {
    const button = document.getElementById(focusId)
      ?? document.getElementById(focusId.replace('gear-equipped-', 'gear-inventory-'));
    button?.focus({ preventScroll: true });
  }
}

async function loadGearPage(page: GearPageState): Promise<void> {
  page.loading = true;
  page.error = '';
  updateGearContents(page);
  try {
    const response = await authenticatedFetch('/api/me/gear', {}, page.userId);
    if (!response.ok) throw await getApiError(response, "Couldn't load your gear. Please try again.");
    const gear = await response.json() as UserGear;
    if (gear.userId !== page.userId) throw new Error('Your account changed. Reopen Gear to try again.');
    page.gear = gear;
  } catch (error) {
    page.error = error instanceof Error ? error.message : "Couldn't load your gear. Please try again.";
  } finally {
    page.loading = false;
    updateGearContents(page);
  }
}

async function saveGearItem(page: GearPageState, itemId: string, focusId: string): Promise<void> {
  if (page.saving || !page.gear || gearPage !== page || currentUser?.id !== page.userId) return;
  const previousGear = page.gear;
  const equippedInstanceIds = toggleGearItem(page.gear, itemId);
  if (!equippedInstanceIds) return;
  page.saving = true;
  page.error = '';
  updateGearContents(page);
  try {
    const response = await authenticatedFetch('/api/me/gear-loadout', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ equippedInstanceIds }),
    }, page.userId);
    if (!response.ok) throw await getApiError(response, "Couldn't save your gear. Please try again.");
    const gear = await response.json() as UserGear;
    if (gear.userId === page.userId) {
      page.gear = gear;
      trackGearEquipmentChanges(previousGear, gear);
    }
  } catch (error) {
    page.error = error instanceof Error ? error.message : "Couldn't save your gear. Please try again.";
  } finally {
    page.saving = false;
    updateGearContents(page, focusId);
  }
}

function attachGearHandlers(root: HTMLElement): void {
  root.querySelector('#gear-back-btn')?.addEventListener('click', () => void navigateHome());
  root.querySelector('#logout-btn')?.addEventListener('click', () => void signOutFromApp());
  attachTalentHudHandler(root);
  if (!gearPage || gearPage.userId !== currentUser!.id) {
    gearPage = { userId: currentUser!.id, gear: null, loading: true, saving: false, error: '' };
    void loadGearPage(gearPage);
  } else updateGearContents(gearPage);
}

function setDisplayedGold(userId: string, gold: number): void {
  if (currentUser?.id !== userId) return;
  if (userProgress?.userId === userId) userProgress = { ...userProgress, gold };
  if (shopPage?.userId === userId && shopPage.data) shopPage.data.gold = gold;
  document.querySelectorAll('[data-gold-balance]').forEach((element) => { element.textContent = String(gold); });
}

function getShopPurchases(userId: string): ShopPurchaseClient {
  if (shopPurchases?.userId === userId) return shopPurchases;
  shopPurchases = new ShopPurchaseClient(userId, window.localStorage, async (pending) => {
    const response = await authenticatedFetch('/api/shop/purchases', {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': pending.purchaseId },
      body: JSON.stringify({ gearId: pending.gearId }), signal: AbortSignal.timeout(20000),
    }, userId);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const definitive = [400, 409].includes(response.status) && ['invalid_purchase', 'insufficient_gold', 'conflict'].includes(body.code);
      throw new PurchaseRequestError(body.error ?? 'Could not confirm your purchase. Check it again to recover the result.', definitive, body.gold);
    }
    return await response.json() as PurchaseResult;
  }, trackGearPurchase);
  return shopPurchases;
}

function updateShopContents(page: ShopPageState): void {
  if (shopPage !== page || currentUser?.id !== page.userId || currentView !== 'shop') return;
  const container = document.getElementById('shop-contents');
  if (!container) return;
  const focusedId = document.activeElement?.id;
  container.innerHTML = renderShopContents(page);
  container.querySelector('#shop-retry-btn')?.addEventListener('click', () => void loadShopPage(page));
  container.querySelector('#shop-check-purchase-btn')?.addEventListener('click', () => void runShopPurchase());
  container.querySelectorAll<HTMLButtonElement>('[data-buy-gear]').forEach((button) => {
    button.addEventListener('click', () => void runShopPurchase(button.dataset.buyGear));
  });
  container.querySelectorAll<HTMLButtonElement>('[data-roll-details]').forEach((button) => {
    button.addEventListener('click', () => {
      const item = page.data?.catalog.find((item) => item.id === button.dataset.rollDetails);
      const dialog = document.querySelector<HTMLDialogElement>('#shop-roll-dialog');
      if (!item || !dialog) return;
      dialog.innerHTML = renderRollDetails(item);
      dialog.querySelector('[data-close-rolls]')?.addEventListener('click', () => dialog.close());
      dialog.showModal();
    });
  });
  if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
}

async function loadShopPage(page: ShopPageState): Promise<void> {
  page.loading = true;
  page.error = '';
  updateShopContents(page);
  try {
    const response = await authenticatedFetch('/api/shop', {}, page.userId);
    if (!response.ok) throw await getApiError(response, "Couldn't load the shop. Please try again.");
    const data = await response.json() as ShopData;
    if (shopPage !== page || currentUser?.id !== page.userId || data.userId !== page.userId) return;
    page.data = data;
    setDisplayedGold(page.userId, data.gold);
    const client = getShopPurchases(page.userId);
    page.pending = client.pending.length > 0;
    page.purchasing = client.busy;
    page.result = client.result;
    if (page.pending && !client.busy) void runShopPurchase();
  } catch (error) {
    page.error = error instanceof Error ? error.message : "Couldn't load the shop. Please try again.";
  } finally {
    page.loading = false;
    updateShopContents(page);
  }
}

async function runShopPurchase(gearId?: string): Promise<void> {
  const userId = currentUser?.id;
  if (!userId || !shopPage?.data || shopPage.purchasing) return;
  shopPage.purchasing = true;
  shopPage.error = '';
  updateShopContents(shopPage);
  let failure = '';
  let client: ShopPurchaseClient | null = null;
  try {
    client = getShopPurchases(userId);
    const result = gearId ? await client.buy(gearId) : await client.recover();
    if (result) {
      setDisplayedGold(userId, result.gold);
      if (currentUser?.id === userId && currentView === 'gear' && gearPage) void loadGearPage(gearPage);
    }
  } catch (error) {
    failure = error instanceof Error ? error.message : 'Could not confirm the purchase. Check it again.';
    if (error instanceof PurchaseRequestError && typeof error.gold === 'number') setDisplayedGold(userId, error.gold);
  } finally {
    if (currentUser?.id === userId && shopPage?.userId === userId) {
      shopPage.purchasing = false;
      shopPage.pending = (client?.pending.length ?? 0) > 0;
      shopPage.result = client?.result ?? null;
      shopPage.error = failure;
      updateShopContents(shopPage);
      if (currentView === 'shop') document.getElementById('shop-purchase-feedback')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
}

function attachShopHandlers(root: HTMLElement): void {
  root.querySelector('#shop-back-btn')?.addEventListener('click', () => void navigateHome());
  root.querySelector('#logout-btn')?.addEventListener('click', () => void signOutFromApp());
  attachTalentHudHandler(root);
  const dialog = root.querySelector<HTMLDialogElement>('#shop-roll-dialog');
  dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
  if (!shopPage || shopPage.userId !== currentUser!.id) {
    shopPage = { userId: currentUser!.id, data: null, loading: true, purchasing: false, pending: false, error: '', result: null };
    void loadShopPage(shopPage);
  } else updateShopContents(shopPage);
}

function renderCompletionModal(): string {
  if (!currentRoom) return '';

  const challenge = currentRoom.challengeSnapshot;
  const earnedRewards = resolveEarnedRewards(challenge, userProgress);

  const rewardRows: string[] = [];

  if (earnedRewards.level > 0) {
    rewardRows.push(`
      <div class="reward">
        <p class="reward-label">Level</p>
        <strong class="reward-value">+${earnedRewards.level}</strong>
      </div>
    `);
  }

  if (earnedRewards.talent > 0) {
    rewardRows.push(`
      <div class="reward">
        <p class="reward-label">Talent Points</p>
        <strong class="reward-value">+${earnedRewards.talent}</strong>
      </div>
    `);
  }

  if (earnedRewards.gold > 0) {
    rewardRows.push(`
      <div class="reward">
        <p class="reward-label">Gold</p>
        <strong class="reward-value">+${earnedRewards.gold}</strong>
      </div>
    `);
  }

  return `
    <div class="modal-overlay">
      <div class="modal card">
        <h2>Workout Complete</h2>
        <p class="modal-text">Your workout reached the goal. Claim your personal reward.</p>
        <div class="rewards-grid">${rewardRows.join('')}</div>
        <button id="claim-btn" class="btn btn-large">Claim Rewards</button>
      </div>
    </div>
  `;
}

async function authenticatedFetch(path: string, init: RequestInit = {}, expectedUserId?: string): Promise<Response> {
  const { data, error } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;

  if (error || !accessToken) {
    throw new Error('Your session has expired. Sign in again.');
  }
  if (expectedUserId && data.session?.user.id !== expectedUserId) {
    throw new Error('Your account changed. Reopen Gear to try again.');
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  return fetch(path, { ...init, headers });
}

async function apiGetProfile(): Promise<UserProfile> {
  const response = await authenticatedFetch('/api/me/profile');
  if (!response.ok) throw await getApiError(response, 'Could not load your profile. Please try again.');
  return await response.json() as UserProfile;
}

async function saveProfile(update: ProfileUpdate): Promise<boolean> {
  const userId = currentUser?.id;
  const sessionVersion = profileSessionVersion;
  const { data, error } = await supabase.auth.getSession();
  if (!userId || currentUser?.id !== userId || profileSessionVersion !== sessionVersion) return false;
  if (error || !data.session) throw new Error('Your session has expired. Sign in again.');
  if (data.session.user.id !== userId) return false;
  const response = await fetch('/api/me/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
    body: JSON.stringify(update),
  });
  if (!response.ok) throw await getApiError(response, 'Could not save your username. Please try again.');
  const profile = await response.json() as UserProfile;
  if (currentUser?.id !== userId || profileSessionVersion !== sessionVersion) return false;
  userProfile = profile;
  return true;
}

function closeUsernameDialog(): void {
  usernameDialog?.close();
  usernameDialog?.remove();
  usernameDialog = null;
}

function openUsernameEditor(): void {
  if (!userProfile || currentView !== 'lobby' || currentRoom || getInviteTokenFromUrl()) return;
  closeUsernameDialog();
  const dialog = document.createElement('dialog');
  dialog.className = 'username-dialog card';
  dialog.setAttribute('aria-labelledby', 'username-title');
  dialog.innerHTML = renderUsernameForm(userProfile, false);
  document.body.append(dialog);
  usernameDialog = dialog;
  dialog.showModal();
  mountUsernameForm(dialog, false, async (update) => {
    if (!await saveProfile(update)) return;
    closeUsernameDialog();
    await renderPage();
    document.getElementById('edit-username-btn')?.focus();
  }, closeUsernameDialog);
}

async function apiGetProgress(): Promise<UserProgress> {
  const response = await authenticatedFetch('/api/me/progress');
  if (!response.ok) throw await getApiError(response, "Couldn't load your progress. Please refresh and try again.");
  return (await response.json()) as UserProgress;
}

async function loadFriends(): Promise<void> {
  const userId = currentUser?.id;
  if (!userId) return;
  const session = profileSessionVersion;
  const version = ++friendsRequestVersion;
  const isCurrent = () => session === profileSessionVersion && version === friendsRequestVersion;
  try {
    const response = await authenticatedFetch('/api/me/friends', {}, userId);
    if (!response.ok) throw new Error('Could not load your friends. Try refreshing.');
    const loaded = await response.json() as Friend[];
    if (!isCurrent()) return;
    friends = loaded;
    friendsError = '';
  } catch {
    if (isCurrent()) friendsError = 'Could not load your friends. Try refreshing.';
  }
}

function syncFriendControls(): void {
  document.querySelectorAll<HTMLElement>('[data-friends-list]').forEach((list) => {
    list.innerHTML = renderFriendsList(friends, friendsError);
  });
  document.querySelectorAll<HTMLElement>('[data-friend-control]').forEach((container) => {
    const person = currentRoom?.participants.find((participant) => participant.userId === container.dataset.friendControl);
    if (!person) return;
    container.innerHTML = renderParticipantFriend(person, friends.some((friend) => friend.userId === person.userId), pendingFriends.has(person.userId));
    container.querySelector<HTMLButtonElement>('button')?.addEventListener('click', () => void addParticipantFriend(person));
  });
}

async function addParticipantFriend(person: Friend): Promise<void> {
  const userId = currentUser?.id;
  const roomId = currentRoom?.id;
  if (!userId || !roomId || pendingFriends.has(person.userId)) return;
  const session = profileSessionVersion;
  pendingFriends.add(person.userId);
  syncFriendControls();
  let message = '';
  try {
    const response = await authenticatedFetch(`/api/rooms/${encodeURIComponent(roomId)}/friends`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ friendUserId: person.userId }),
    }, userId);
    if (!response.ok) throw await getApiError(response, 'Could not add friend. Please try again.');
    const added = await response.json() as Friend;
    if (session !== profileSessionVersion) return;
    ++friendsRequestVersion;
    friends = [...friends.filter((friend) => friend.userId !== added.userId), added]
      .sort((a, b) => a.username.localeCompare(b.username));
    message = `${added.username} added to your friends.`;
  } catch (error) {
    message = error instanceof Error ? error.message : 'Could not add friend. Please try again.';
  } finally {
    if (session === profileSessionVersion) {
      pendingFriends.delete(person.userId);
      syncFriendControls();
      if (currentRoom?.id === roomId) {
        const status = document.getElementById('friend-action-status');
        if (status) status.textContent = message;
      }
    }
  }
}

async function apiSaveTalentLoadout(talentLoadout: TalentLoadout): Promise<UserProgress> {
  const response = await authenticatedFetch('/api/me/talent-loadout', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ talentLoadout }),
  });

  if (!response.ok) throw await getApiError(response, "Couldn't save your talents. Please try again.");
  return (await response.json()) as UserProgress;
}

async function apiSavePushSubscription(subscription: PushSubscriptionLike): Promise<void> {
  const response = await authenticatedFetch('/api/me/push-subscription', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subscription.toJSON()),
  });
  if (!response.ok) {
    throw await getApiError(response, "Couldn't finish notification setup. Please try again.");
  }
}

async function apiDeactivatePushSubscription(endpoint: string): Promise<void> {
  const response = await authenticatedFetch('/api/me/push-subscription', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  });
  if (!response.ok) {
    throw await getApiError(response, "Couldn't stop notifications on this device.");
  }
}

async function apiCreateRoom(challengeType: ChallengeType): Promise<Room> {
  const response = await authenticatedFetch('/api/rooms/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ challengeType }),
  });

  if (!response.ok) throw await getApiError(response, "Couldn't start the workout. Please try again.");
  return (await response.json()) as Room;
}

class ApiResponseError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

async function getApiError(response: Response, fallbackMessage: string): Promise<Error> {
  try {
    const payload = (await response.json()) as { error?: string };
    if (response.status < 500 && payload?.error) return new ApiResponseError(payload.error, response.status);
  } catch {
    // Use the player-friendly fallback when the response has no useful message.
  }
  return new ApiResponseError(fallbackMessage, response.status);
}

async function apiRedeemRoomInvite(token: string): Promise<RedeemedInvite> {
  const response = await authenticatedFetch('/api/room-invites/redeem', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) throw await getApiError(response, "Couldn't join the workout. Check the link and try again.");
  return (await response.json()) as RedeemedInvite;
}

async function apiResolveRoomInvite(token: string): Promise<InviteResolution> {
  const request = currentUser ? authenticatedFetch : fetch;
  const response = await request('/api/room-invites/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw await getApiError(response, 'Could not check this invitation.');
  return await response.json() as InviteResolution;
}

async function apiGetRoom(roomId: string): Promise<Room> {
  const response = await authenticatedFetch(`/api/rooms/${roomId}`);
  if (!response.ok) throw await getApiError(response, "Couldn't open that workout. Please try again.");
  return (await response.json()) as Room;
}

async function apiListRoomsForUser(): Promise<Room[]> {
  const response = await authenticatedFetch('/api/rooms');
  if (!response.ok) throw await getApiError(response, "Couldn't load your workouts. Please refresh and try again.");
  return (await response.json()) as Room[];
}

async function apiClub(path: string, init: RequestInit = {}): Promise<DailyClub> {
  const response = await authenticatedFetch(path, init, currentUser?.id);
  if (!response.ok) throw await getApiError(response, 'Could not open this club. Please try again.');
  return await response.json() as DailyClub;
}

async function loadJoinedClubs(): Promise<void> {
  const session = profileSessionVersion;
  try {
    const response = await authenticatedFetch('/api/clubs', {}, currentUser?.id);
    if (!response.ok) throw new Error('Could not load clubs');
    const clubs = await response.json() as DailyClub[];
    if (session !== profileSessionVersion) return;
    joinedClubs = clubs;
    clubsError = '';
  } catch {
    if (session === profileSessionVersion) clubsError = 'Could not load your clubs. Try refreshing.';
  }
}

function applyClub(club: DailyClub): void {
  if (currentRoom?.id !== club.room?.id) {
    resetWorkoutTimer();
    pendingActivitySubmission = null;
    rewardModalShown = false;
  }
  currentClub = club;
  currentRoom = club.room;
  clubClockOffset = club.serverNow - Date.now();
}

async function openClub(id: string, mode: UrlNavigationMode = 'push'): Promise<void> {
  const session = profileSessionVersion;
  const version = ++clubRequestVersion;
  const club = await apiClub(`/api/clubs/${encodeURIComponent(id)}`);
  if (session !== profileSessionVersion || version !== clubRequestVersion) return;
  resetCurrentRoomState();
  applyClub(club);
  currentView = 'workout';
  workoutPageOrigin = 'lobby';
  setClubIdInUrl(club.id, mode);
  await loadInvitesForCurrentRoom();
  await renderPage();
}

async function refreshClub(forceRender = false): Promise<void> {
  const id = currentClub?.id;
  if (!id || getClubIdFromUrl() !== id) return;
  const session = profileSessionVersion;
  const version = ++clubRequestVersion;
  const before = JSON.stringify({ ...currentClub, serverNow: 0 });
  const day = currentClub!.day;
  try {
    const response = await authenticatedFetch(`/api/clubs/${encodeURIComponent(id)}`, {}, currentUser?.id);
    if (session !== profileSessionVersion || version !== clubRequestVersion || getClubIdFromUrl() !== id) return;
    if (response.status === 404) { await navigateHome(); return; }
    if (!response.ok) throw await getApiError(response, 'Could not refresh the club.');
    const club = await response.json() as DailyClub;
    if (session !== profileSessionVersion || version !== clubRequestVersion || getClubIdFromUrl() !== id) return;
    applyClub(club);
    if (club.day !== day) clubNotice = 'A new club day has started. Log today’s activity below.';
    const invitesBefore = JSON.stringify(roomInvites);
    await loadInvitesForCurrentRoom();
    if (getClubIdFromUrl() !== id || session !== profileSessionVersion) return;
    if (forceRender || before !== JSON.stringify({ ...club, serverNow: 0 }) || invitesBefore !== JSON.stringify(roomInvites)) await renderPage();
  } catch {
    // Existing data remains visible; the regular poll and tab return can retry.
  } finally {
    if (currentClub?.id === id && !document.hidden) startPolling();
  }
}

async function apiCreateRoomInvite(
  roomId: string,
  expiration: InviteExpiration,
  club = false,
): Promise<CreatedRoomInvite | CreatedClubInvite> {
  const response = await authenticatedFetch(`/api/${club ? 'clubs' : 'rooms'}/${roomId}/invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ expiration }),
  });
  if (!response.ok) throw await getApiError(response, "Couldn't create an invite link. Please try again.");
  return (await response.json()) as CreatedRoomInvite | CreatedClubInvite;
}

async function apiListRoomInvites(roomId: string, club = false): Promise<Array<RoomInvite | ClubInvite>> {
  const response = await authenticatedFetch(`/api/${club ? 'clubs' : 'rooms'}/${roomId}/invites`);
  if (!response.ok) throw await getApiError(response, "Couldn't load your invite links. Please try again.");
  return (await response.json()) as RoomInvite[];
}

async function apiRevokeRoomInvite(roomId: string, inviteId: string, club = false): Promise<void> {
  const response = await authenticatedFetch(`/api/${club ? 'clubs' : 'rooms'}/${roomId}/invites/${inviteId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw await getApiError(response, "Couldn't revoke that invite. Please try again.");
}

async function loadJoinedRoomsForUser(): Promise<void> {
  joinedRooms = await apiListRoomsForUser();
}

async function loadInvitesForCurrentRoom(): Promise<void> {
  const targetId = currentClub?.id ?? currentRoom?.id;
  if (!targetId || !currentUser || (currentRoom?.clubId && !currentClub)
    || (currentClub?.hostUserId ?? currentRoom?.createdBy) !== currentUser.id) {
    roomInvites = [];
    latestCreatedInvite = null;
    return;
  }

  const session = profileSessionVersion;
  const latestInvites = await apiListRoomInvites(targetId, Boolean(currentClub));
  if ((currentClub?.id ?? currentRoom?.id) !== targetId || session !== profileSessionVersion) return;

  roomInvites = latestInvites;
  if (latestCreatedInvite) {
    const latestStatus = roomInvites.find((invite) => invite.id === latestCreatedInvite?.id)?.status;
    if (latestStatus && latestStatus !== 'available') latestCreatedInvite = null;
  }
}

async function apiAddActivity(
  roomId: string,
  type: ActivityType,
  qty: number,
  idempotencyKey: string,
): Promise<Room> {
  const response = await authenticatedFetch(`/api/rooms/${roomId}/activities`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify({ type, qty }),
  });

  if (!response.ok) throw await getApiError(response, "Couldn't log that activity. Please try again.");
  return (await response.json()) as Room;
}

async function apiClaimRoomReward(roomId: string): Promise<WorkoutRewardClaimResult> {
  const response = await authenticatedFetch(`/api/rooms/${roomId}/rewards/claim`, {
    method: 'POST',
  });

  if (!response.ok) throw await getApiError(response, "Couldn't claim your rewards. Please try again.");
  return (await response.json()) as WorkoutRewardClaimResult;
}

function stopPolling(): void {
  if (clubResetTimer) { clearTimeout(clubResetTimer); clubResetTimer = null; }
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

async function refreshCurrentRoom(): Promise<void> {
  if (currentClub) { await refreshClub(); return; }
  const roomId = currentRoom?.id;
  if (!roomId) return;

  try {
    const latest = await apiGetRoom(roomId);
    if (currentRoom?.id !== roomId) return;

    const previousInvites = JSON.stringify(roomInvites);
    const changed = latest.revision !== currentRoom.revision ||
      latest.status !== currentRoom.status ||
      latest.rewardClaimed !== currentRoom.rewardClaimed;

    currentRoom = latest;
    await loadInvitesForCurrentRoom();
    if (currentRoom?.id !== roomId) return;

    const hasActiveInviteCountdown = roomInvites.some((invite) => invite.status === 'available');
    if (changed || previousInvites !== JSON.stringify(roomInvites) || hasActiveInviteCountdown) {
      await renderPage();
    }
  } catch {
    // Keep polling even if one refresh fails.
  }
}

function startPolling(): void {
  stopPolling();

  if ((!currentRoom && !currentClub) || document.hidden) return;

  pollTimer = setInterval(() => void refreshCurrentRoom(), POLL_MS);
  if (currentClub) {
    const untilMidnight = currentClub.nextResetAt - (Date.now() + clubClockOffset);
    clubResetTimer = setTimeout(() => void refreshClub(true), Math.max(1000, untilMidnight + 100));
  }
}

async function handleVisibilityChange(): Promise<void> {
  if (document.hidden) {
    stopPolling();
    return;
  }

  if (!currentUser) emailSignIn.refresh();
  refreshNotificationDeliveryState();
  syncCurrentWorkoutNotificationControl();
  void reconcileNotificationDelivery();

  if (!currentRoom && !currentClub) return;

  await refreshCurrentRoom();
  if ((currentRoom || currentClub) && !document.hidden) startPolling();
}

function showCompletionModalIfNeeded(): void {
  if (!currentUser || !currentRoom || !currentRoom.completed || rewardModalShown) return;
  if (currentRoom.rewardClaimed) return;

  rewardModalShown = true;

  const root = document.getElementById('app');
  if (!root) return;

  const container = document.createElement('div');
  container.innerHTML = renderCompletionModal();
  root.appendChild(container);

  const claimBtn = root.querySelector<HTMLButtonElement>('#claim-btn');
  if (!claimBtn) return;
  const roomId = currentRoom.id;
  const session = profileSessionVersion;

  claimBtn.addEventListener('click', async () => {
    if (!currentUser || session !== profileSessionVersion) return;
    claimBtn.disabled = true;

    try {
      const claim = await apiClaimRoomReward(roomId);
      if (session !== profileSessionVersion) return;
      userProgress = claim.progress;
      if (currentRoom?.id === roomId) {
        currentRoom = { ...currentRoom, rewardClaimed: true };
        if (currentClub) currentClub = { ...currentClub, room: currentRoom };
      }
      container.remove();
      await renderPage();
    } catch (error) {
      if (session !== profileSessionVersion) return;
      claimBtn.disabled = false;
      const message = error instanceof Error ? error.message : 'Could not claim workout reward.';
      alert(message);
    }
  });
}

function attachLoginHandlers(root: HTMLElement): void {
  const element = root.querySelector<HTMLElement>('[data-email-sign-in]');
  if (element === mountedSignInElement) return;
  unmountSignIn?.();
  mountedSignInElement = element;
  unmountSignIn = element ? mountSignInForm(element, emailSignIn, () => {
    const inviteToken = getInviteTokenFromUrl();
    return inviteToken
      ? getInviteAuthRedirectUrl(window.location.origin, inviteToken)
      : window.location.origin;
  }) : null;
}

function attachInviteHandlers(root: HTMLElement): void {
  const redeemInviteBtn = root.querySelector<HTMLButtonElement>('#redeem-invite-btn');
  const logoutBtn = root.querySelector<HTMLButtonElement>('#logout-btn');

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      await signOutFromApp();
    });
  }

  if (!redeemInviteBtn || !currentUser) return;

  redeemInviteBtn.addEventListener('click', async () => {
    const token = getInviteTokenFromUrl();
    if (!token || redeemInviteBtn.disabled) return;
    const sessionVersion = profileSessionVersion;
    const isCurrentInvite = () => sessionVersion === profileSessionVersion && getInviteTokenFromUrl() === token;

    try {
      redeemInviteBtn.disabled = true;
      const result = await apiRedeemRoomInvite(token);
      if (!isCurrentInvite()) return;
      clearInviteTokenFromUrl();
      resetCurrentRoomState();
      currentView = 'workout';
      workoutPageOrigin = 'lobby';
      if ('club' in result) { applyClub(result.club); setClubIdInUrl(result.club.id); }
      else { currentRoom = result; setRoomIdInUrl(result.id, 'lobby'); }
      rewardModalShown = false;
      await loadInvitesForCurrentRoom();
      startPolling();
      await renderPage();
    } catch (err) {
      if (!isCurrentInvite()) return;
      const message = err instanceof Error ? err.message : 'Could not join workout. Check the link and try again.';
      alert(message);
      await renderPage();
    }
  });
}

function attachInviteHomeHandler(root: HTMLElement): void {
  const homeBtn = root.querySelector<HTMLButtonElement>('#invite-home-btn');
  if (!homeBtn) return;

  homeBtn.addEventListener('click', () => void navigateHome());
}

function resetWorkoutTimer(): void {
  workoutTimer?.widget.dispose();
  workoutTimer = null;
}

function resetCurrentRoomState(): void {
  ++clubRequestVersion;
  currentClub = null;
  clubNotice = '';
  resetWorkoutTimer();
  stopPolling();
  gearPage = null;
  shopPage = null;
  currentRoom = null;
  rewardModalShown = false;
  roomInvites = [];
  latestCreatedInvite = null;
  selectedInviteExpiration = DEFAULT_INVITE_EXPIRATION;
  selectedInviteFriendUserId = '';
  latestInviteFriendName = '';
  pendingActivitySubmission = null;
}

async function navigateHome(): Promise<void> {
  resetCurrentRoomState();
  currentView = 'lobby';
  workoutPageOrigin = 'lobby';
  clearInviteTokenFromUrl();
  clearAppNavigationFromUrl();
  await renderPage();
}

async function navigateToPrimaryView(
  view: PrimaryView,
  mode: UrlNavigationMode = 'push',
): Promise<void> {
  resetCurrentRoomState();
  currentView = view;
  workoutPageOrigin = view;
  setPrimaryViewInUrl(view, mode);
  await renderPage();
}

async function signOutFromApp(): Promise<void> {
  emailSignIn.clear();
  await deactivateNotificationsForCurrentDevice();
  resetCurrentRoomState();
  currentView = 'lobby';
  workoutPageOrigin = 'lobby';
  clearAppNavigationFromUrl();
  await supabase.auth.signOut();
  await renderPage();
}

async function deactivateNotificationsForCurrentDevice(): Promise<void> {
  if (!currentUser) {
    resetNotificationDeliveryClient();
    return;
  }

  try {
    const registration = await notificationServiceWorker;
    await deactivatePushSubscriptionForSignOut(
      registration,
      currentPushSubscription,
      apiDeactivatePushSubscription,
    );
  } catch {
    // Signing out must still succeed if browser notification cleanup fails.
  } finally {
    resetNotificationDeliveryClient();
  }
}

function resetNotificationDeliveryClient(): void {
  notificationDeliveryState = 'unsupported';
  notificationServiceWorker = null;
  currentPushSubscription = null;
  notificationEnrollmentRequest = null;
}

function attachWorkoutOpenHandlers(root: HTMLElement, origin: PrimaryView): void {
  const openLinks = root.querySelectorAll<HTMLAnchorElement>('.saved-room-open');
  if (!currentUser) return;

  openLinks.forEach((link) => {
    link.addEventListener('click', async (event) => {
      if (!shouldHandleNavigationClick(event)) return;
      event.preventDefault();

      const roomId = link.dataset.roomId;
      if (!roomId) return;

      try {
        resetCurrentRoomState();
        currentRoom = await apiGetRoom(roomId);
        currentView = 'workout';
        workoutPageOrigin = origin;
        setRoomIdInUrl(currentRoom.id, origin, 'push');
        rewardModalShown = false;
        await loadInvitesForCurrentRoom();
        startPolling();
        await renderPage();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not open this workout. It may no longer exist.';
        alert(message);
      }
    });
  });
}

function syncLobbyCollapsibleCardControls(root: HTMLElement): void {
  const collapsibleCardToggles = root.querySelectorAll<HTMLButtonElement>('.collapsible-card-toggle');

  collapsibleCardToggles.forEach((toggle) => {
    toggle.disabled = !MOBILE_COLLAPSIBLE_MEDIA.matches;

    if (!MOBILE_COLLAPSIBLE_MEDIA.matches) {
      const card = toggle.closest<HTMLElement>('[data-collapsible-card]');
      toggle.setAttribute('aria-expanded', 'true');
      card?.classList.remove('is-collapsed');
    }
  });
}

function refreshNotificationDeliveryState(): NotificationDeliveryState {
  if (!currentUser) {
    notificationDeliveryState = 'unsupported';
    return notificationDeliveryState;
  }

  const permission = getNotificationPermissionState();
  notificationDeliveryState = getNotificationDeliveryState({
    supported: isNotificationDeliverySupported(VAPID_PUBLIC_KEY),
    permission,
    hasSubscription: Boolean(currentPushSubscription),
    syncFailed: notificationDeliveryState === 'retryable-error',
  });
  return notificationDeliveryState;
}

function prepareNotificationServiceWorker(): Promise<ServiceWorkerRegistrationLike | null> {
  if (!currentUser || !isNotificationDeliverySupported(VAPID_PUBLIC_KEY)) {
    return Promise.resolve(null);
  }
  if (notificationServiceWorker) return notificationServiceWorker;

  notificationServiceWorker = registerNotificationServiceWorker(VAPID_PUBLIC_KEY)
    .catch((error) => {
      notificationServiceWorker = null;
      throw error;
    });
  return notificationServiceWorker;
}

async function reconcileNotificationDelivery(): Promise<void> {
  const userId = currentUser?.id;
  if (!userId) return;

  refreshNotificationDeliveryState();
  syncCurrentWorkoutNotificationControl();
  if (!isNotificationDeliverySupported(VAPID_PUBLIC_KEY)) return;

  try {
    const registration = await prepareNotificationServiceWorker();
    if (!registration || currentUser?.id !== userId) return;

    const permission = getNotificationPermissionState();
    if (permission !== 'granted') {
      currentPushSubscription = null;
      notificationDeliveryState = getNotificationDeliveryState({
        supported: true,
        permission,
      });
      syncCurrentWorkoutNotificationControl();
      return;
    }

    const subscription = await getExistingPushSubscription(registration);
    if (currentUser?.id !== userId) return;
    currentPushSubscription = subscription;
    if (!subscription) {
      notificationDeliveryState = 'setup-required';
      syncCurrentWorkoutNotificationControl();
      return;
    }

    notificationDeliveryState = 'enabling';
    syncCurrentWorkoutNotificationControl();
    await apiSavePushSubscription(subscription);
    if (currentUser?.id !== userId) return;
    notificationDeliveryState = 'enabled';
  } catch {
    if (currentUser?.id === userId) notificationDeliveryState = 'retryable-error';
  }
  syncCurrentWorkoutNotificationControl();
}

function attachNotificationPermissionHandler(root: HTMLElement): void {
  const button = root.querySelector<HTMLButtonElement>('#enable-notifications-btn');
  const actionable = notificationDeliveryState === 'default'
    || notificationDeliveryState === 'setup-required'
    || notificationDeliveryState === 'retryable-error';
  if (!button || !actionable) return;

  button.addEventListener('click', () => {
    beginNotificationEnrollment(root);
  });
}

function syncWorkoutNotificationControl(root: HTMLElement): void {
  const control = root.querySelector<HTMLElement>('#workout-notification-permission');
  if (!control) return;

  control.outerHTML = renderWorkoutNotificationControl(notificationDeliveryState);
  attachNotificationPermissionHandler(root);
}

function syncCurrentWorkoutNotificationControl(): void {
  const root = document.getElementById('app');
  if (root) syncWorkoutNotificationControl(root);
}

function beginNotificationEnrollment(root = document.getElementById('app')): void {
  const userId = currentUser?.id;
  if (!userId || notificationEnrollmentRequest) return;

  const supported = isNotificationDeliverySupported(VAPID_PUBLIC_KEY);
  const currentPermission = getNotificationPermissionState();
  if (!supported || currentPermission === 'denied' || currentPermission === 'unsupported') {
    refreshNotificationDeliveryState();
    if (root) syncWorkoutNotificationControl(root);
    return;
  }

  notificationDeliveryState = 'enabling';
  if (root) syncWorkoutNotificationControl(root);

  // Keep the native permission call directly inside the originating click handler.
  const permissionRequest = requestNotificationPermission();
  const registrationRequest = prepareNotificationServiceWorker();

  notificationEnrollmentRequest = (async () => {
    const permission = await permissionRequest;
    if (currentUser?.id !== userId) return;
    if (permission !== 'granted') {
      currentPushSubscription = null;
      notificationDeliveryState = getNotificationDeliveryState({
        supported,
        permission,
      });
      return;
    }

    const registration = await registrationRequest;
    if (!registration) throw new Error('Notification service worker is unavailable');
    const existingSubscription = await getExistingPushSubscription(registration);
    const subscription = existingSubscription
      ?? await createPushSubscription(registration, VAPID_PUBLIC_KEY);
    if (currentUser?.id !== userId) return;

    await apiSavePushSubscription(subscription);
    if (currentUser?.id !== userId) return;
    currentPushSubscription = subscription;
    notificationDeliveryState = 'enabled';
  })().catch(() => {
    if (currentUser?.id === userId) notificationDeliveryState = 'retryable-error';
  }).finally(() => {
    notificationEnrollmentRequest = null;
    syncCurrentWorkoutNotificationControl();
  });
}

function attachLobbyHandlers(root: HTMLElement): void {
  root.querySelector<HTMLSelectElement>('#creation-mode')?.addEventListener('change', (event) => {
    creationMode = (event.target as HTMLSelectElement).value === 'club' ? 'club' : 'workout';
    void renderPage().then(() => document.getElementById('creation-mode')?.focus());
  });
  root.querySelectorAll<HTMLAnchorElement>('.club-open').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (!shouldHandleNavigationClick(event) || !link.dataset.clubId) return;
      event.preventDefault();
      void openClub(link.dataset.clubId).catch((error) => alert(error.message));
    });
  });
  root.querySelector('#edit-username-btn')?.addEventListener('click', openUsernameEditor);
  const logoutBtn = root.querySelector<HTMLButtonElement>('#logout-btn');
  const createButtons = root.querySelectorAll<HTMLButtonElement>('.create-room-btn');
  const historyLink = root.querySelector<HTMLAnchorElement>('#workout-history-link');
  const collapsibleCardToggles = root.querySelectorAll<HTMLButtonElement>('.collapsible-card-toggle');

  attachTalentHudHandler(root);
  attachWorkoutOpenHandlers(root, 'lobby');
  syncLobbyCollapsibleCardControls(root);

  collapsibleCardToggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      if (!MOBILE_COLLAPSIBLE_MEDIA.matches) return;

      const card = toggle.closest<HTMLElement>('[data-collapsible-card]');
      if (!card) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));
      card.classList.toggle('is-collapsed', isExpanded);
    });
  });

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      await signOutFromApp();
    });
  }

  if (historyLink) {
    historyLink.addEventListener('click', async (event) => {
      if (!shouldHandleNavigationClick(event)) return;
      event.preventDefault();
      await navigateToPrimaryView('history');
    });
  }

  if (createButtons.length && currentUser) {
    createButtons.forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        const challengeType = button.dataset.challengeType as ChallengeType | undefined;
        if (!challengeType) return;

        beginNotificationEnrollment(root);

        try {
          createButtons.forEach((control) => { control.disabled = true; });
          const modeSelect = root.querySelector<HTMLSelectElement>('#creation-mode');
          if (modeSelect) modeSelect.disabled = true;
          const session = profileSessionVersion;
          const mode = creationMode;
          const result = mode === 'club' ? await apiClub('/api/clubs', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challengeType, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
          }) : await apiCreateRoom(challengeType);
          if (session !== profileSessionVersion) return;
          resetCurrentRoomState();
          currentView = 'workout';
          workoutPageOrigin = 'lobby';
          if (mode === 'club') { applyClub(result as DailyClub); setClubIdInUrl(result.id, 'push'); }
          else { currentRoom = result as Room; setRoomIdInUrl(result.id, 'lobby'); }
          rewardModalShown = false;
          await loadInvitesForCurrentRoom();
          startPolling();
          await renderPage();
        } catch (err) {
          createButtons.forEach((control) => { control.disabled = false; });
          const modeSelect = root.querySelector<HTMLSelectElement>('#creation-mode');
          if (modeSelect) modeSelect.disabled = false;
          const message = err instanceof Error ? err.message : 'Could not create workout';
          alert(message);
        }
      });
    });
  }

}

function attachWorkoutHistoryHandlers(root: HTMLElement): void {
  const backLink = root.querySelector<HTMLAnchorElement>('#history-back-btn');
  const logoutBtn = root.querySelector<HTMLButtonElement>('#logout-btn');

  attachTalentHudHandler(root);
  attachWorkoutOpenHandlers(root, 'history');

  if (backLink) {
    backLink.addEventListener('click', async (event) => {
      if (!shouldHandleNavigationClick(event)) return;
      event.preventDefault();
      await navigateToPrimaryView('lobby');
    });
  }

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      await signOutFromApp();
    });
  }
}

function attachTalentTreeHandlers(root: HTMLElement): void {
  root.querySelector<HTMLButtonElement>('#talent-reset-btn')?.addEventListener('click', async (event) => {
    if (talentSavePending || !userProgress || !getTalentLoadoutCost(userProgress.talentLoadout)
      || userProgress.gold < TALENT_RESET_COST) return;
    if (!confirm(`Reset your talent tree for ${TALENT_RESET_COST} gold? All spent talent points will be refunded.`)) return;
    const button = event.currentTarget as HTMLButtonElement;
    const userId = currentUser?.id;
    talentSavePending = true;
    button.disabled = true;
    button.textContent = 'Resetting…';
    try {
      const response = await authenticatedFetch('/api/me/talent-reset', { method: 'POST' });
      if (!response.ok) throw await getApiError(response, 'Could not reset your talent tree. Please try again.');
      const progress = await response.json() as UserProgress;
      if (currentUser?.id === userId) userProgress = progress;
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Could not reset your talent tree.');
    } finally {
      talentSavePending = false;
      if (currentUser?.id === userId && currentView === 'talent-tree') await renderPage();
    }
  });
  const backBtn = root.querySelector<HTMLButtonElement>('#talent-back-btn');
  const logoutBtn = root.querySelector<HTMLButtonElement>('#logout-btn');
  const talentNodes = root.querySelectorAll<HTMLElement>('.talent-node-interactive');
  const talentPips = root.querySelectorAll<HTMLButtonElement>('.talent-pip-button');

  if (backBtn) {
    backBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      await navigateHome();
    });
  }

  if (talentNodes.length && currentUser) {
    talentNodes.forEach((node) => {
      node.addEventListener('click', async () => {
        const talentId = node.dataset.talentId;
        const maxPoints = Number(node.dataset.maxPoints ?? '0');
        if (!talentId) return;

        const progress = userProgress;
        if (!progress) return;
        const currentValue = progress.talentLoadout[talentId] ?? 0;
        const nextValue = Number.isFinite(maxPoints) && maxPoints > 0
          ? Math.min(maxPoints, currentValue + 1)
          : 1;

        if (!await saveTalentRank(talentId, nextValue)) return;
        await renderPage();
      });

      node.addEventListener('keydown', async (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();

        const talentId = node.dataset.talentId;
        const maxPoints = Number(node.dataset.maxPoints ?? '0');
        if (!talentId) return;

        const progress = userProgress;
        if (!progress) return;
        const currentValue = progress.talentLoadout[talentId] ?? 0;
        const nextValue = Number.isFinite(maxPoints) && maxPoints > 0
          ? Math.min(maxPoints, currentValue + 1)
          : 1;

        if (!await saveTalentRank(talentId, nextValue)) return;
        await renderPage();
      });
    });
  }

  if (talentPips.length && currentUser) {
    talentPips.forEach((pip) => {
      pip.addEventListener('click', async (event) => {
        event.stopPropagation();

        const talentId = pip.dataset.talentId;
        const pipValue = Number(pip.dataset.pipValue ?? '0');
        if (!talentId || !Number.isFinite(pipValue)) return;

        const progress = userProgress;
        if (!progress) return;
        const nextValue = pipValue;

        if (!await saveTalentRank(talentId, nextValue)) return;
        await renderPage();
      });
    });
  }

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      await signOutFromApp();
    });
  }
}

function attachWorkoutHandlers(root: HTMLElement): void {
  syncFriendControls();
  root.querySelector<HTMLButtonElement>('.leave-club-btn')?.addEventListener('click', async (event) => {
    const club = currentClub;
    if (!club || !confirm('Leave this club at the next midnight? Today’s activity and rewards will stay available.')) return;
    const session = profileSessionVersion;
    const button = event.currentTarget as HTMLButtonElement;
    button.disabled = true;
    try {
      const updated = await apiClub(`/api/clubs/${club.id}/leave`, { method: 'POST' });
      if (session !== profileSessionVersion || currentClub?.id !== club.id) return;
      applyClub(updated);
      await renderPage();
    } catch (error) {
      if (session !== profileSessionVersion || currentClub?.id !== club.id) return;
      button.disabled = false;
      alert(error instanceof Error ? error.message : 'Could not leave the club.');
    }
  });
  const backBtn = root.querySelector<HTMLButtonElement>('#back-to-home-btn');
  const logoutBtn = root.querySelector<HTMLButtonElement>('#logout-btn');
  const form = root.querySelector<HTMLFormElement>('#activity-form');
  const amountInput = root.querySelector<HTMLInputElement>('#activity-amount');
  const typeSelect = root.querySelector<HTMLSelectElement>('#activity-type');
  const createInviteBtn = root.querySelector<HTMLButtonElement>('#create-invite-btn');
  const inviteExpirationSelect = root.querySelector<HTMLSelectElement>('#invite-expiration');
  const inviteFriendSelect = root.querySelector<HTMLSelectElement>('#invite-friend');
  const copyInviteBtn = root.querySelector<HTMLButtonElement>('#copy-invite-btn');
  const inviteInput = root.querySelector<HTMLInputElement>('#invite-link');
  const revokeInviteButtons = root.querySelectorAll<HTMLButtonElement>('.revoke-invite-btn');

  attachTalentHudHandler(root);
  attachNotificationPermissionHandler(root);
  inviteFriendSelect?.addEventListener('change', () => {
    selectedInviteFriendUserId = inviteFriendSelect.value;
  });

  if (inviteExpirationSelect) {
    inviteExpirationSelect.addEventListener('change', () => {
      if (isInviteExpiration(inviteExpirationSelect.value)) {
        selectedInviteExpiration = inviteExpirationSelect.value;
      }
    });
  }

  if (backBtn) {
    backBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      if (canReturnToWorkoutOriginWithBrowserHistory()) {
        window.history.back();
        return;
      }
      await navigateToPrimaryView(workoutPageOrigin, 'replace');
    });
  }

  if (logoutBtn) {
    logoutBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      await signOutFromApp();
    });
  }

  if (createInviteBtn && (currentRoom || currentClub)) {
    createInviteBtn.addEventListener('click', async () => {
      if (createInviteBtn.disabled || (!currentRoom && !currentClub)) return;
      const roomId = (currentClub?.id ?? currentRoom!.id);
      const isClub = Boolean(currentClub);
      const session = profileSessionVersion;
      const friendName = friends.find((friend) => friend.userId === selectedInviteFriendUserId)?.username ?? '';
      const isCurrent = () => profileSessionVersion === session && (currentClub?.id ?? currentRoom?.id) === roomId;
      try {
        createInviteBtn.disabled = true;
        const invite = await apiCreateRoomInvite(
          roomId,
          selectedInviteExpiration,
          isClub,
        );
        if (!isCurrent()) return;
        latestCreatedInvite = invite;
        latestInviteFriendName = friendName;
        const inviteUrl = `${window.location.origin}/?invite=${encodeURIComponent(latestCreatedInvite.token)}`;
        await navigator.clipboard.writeText(inviteUrl).catch(() => undefined);
        if (!isCurrent()) return;
        await loadInvitesForCurrentRoom();
        if (!isCurrent()) return;
        await renderPage();
      } catch (error) {
        if (!isCurrent()) return;
        createInviteBtn.disabled = false;
        alert(error instanceof Error ? error.message : 'Could not create invitation.');
      }
    });
  }

  if (copyInviteBtn && inviteInput) {
    copyInviteBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(inviteInput.value);
        copyInviteBtn.textContent = 'Copied';
      } catch {
        inviteInput.select();
        document.execCommand('copy');
      }
    });
  }

  revokeInviteButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const inviteId = button.dataset.inviteId;
      const targetId = currentClub?.id ?? currentRoom?.id;
      if (!targetId || !inviteId) return;
      try {
        button.disabled = true;
        await apiRevokeRoomInvite(targetId, inviteId, Boolean(currentClub));
        if (latestCreatedInvite?.id === inviteId) latestCreatedInvite = null;
        await loadInvitesForCurrentRoom();
        await renderPage();
      } catch (error) {
        button.disabled = false;
        alert(error instanceof Error ? error.message : 'Could not revoke invitation.');
      }
    });
  });

  if (!form || !amountInput || !typeSelect || !currentRoom || !currentUser) {
    return;
  }

  const room = currentRoom;
  mountActivityForm(form);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (room.status !== 'active') {
      alert(room.status === 'completed'
        ? 'This workout is already complete.'
        : 'This workout has expired. Create a new daily workout to continue.');
      return;
    }

    const activity = readActivityFormSubmission(form);
    if (!activity) return;
    const { type, qty } = activity;

    const matchesPendingSubmission = pendingActivitySubmission?.roomId === room.id
      && pendingActivitySubmission.type === type
      && pendingActivitySubmission.qty === qty;
    if (!matchesPendingSubmission) {
      pendingActivitySubmission = {
        roomId: room.id,
        type,
        qty,
        idempotencyKey: crypto.randomUUID(),
      };
    }
    const submission = pendingActivitySubmission;
    if (!submission) return;

    try {
      const session = profileSessionVersion;
      const updated = await apiAddActivity(
        room.id,
        type,
        qty,
        submission.idempotencyKey,
      );
      if (session !== profileSessionVersion || currentRoom?.id !== room.id) return;
      currentRoom = updated;
      if (currentClub) currentClub = { ...currentClub, room: updated };
      pendingActivitySubmission = null;
      amountInput.value = '10';
      typeSelect.value = 'Pushups';
      await renderPage();
    } catch (err) {
      if (room.clubId && currentClub?.id === room.clubId && (Date.now() + clubClockOffset >= room.expiresAt || (err instanceof ApiResponseError && err.status === 410))) {
        clubNotice = 'That club day has ended. This activity was not logged. Review it before submitting for today.';
        await refreshClub(true);
        const refreshed = document.getElementById('activity-amount') as HTMLInputElement | null;
        const refreshedType = document.getElementById('activity-type') as HTMLSelectElement | null;
        if (refreshed && refreshedType) {
          refreshedType.value = type;
          refreshedType.dispatchEvent(new Event('change'));
          refreshed.value = String(qty);
        }
        alert('That club day has ended. This activity was not logged. Review it before submitting for today.');
        return;
      }
      const message = err instanceof Error ? err.message : 'Could not submit activity.';
      alert(message);
    }
  });
}

async function renderPage(): Promise<void> {
  const renderVersion = ++pageRenderVersion;
  const renderUserId = currentUser?.id;
  const renderSessionVersion = profileSessionVersion;
  const isCurrentRender = () => renderVersion === pageRenderVersion && currentUser?.id === renderUserId
    && profileSessionVersion === renderSessionVersion;
  const root = document.getElementById('app');
  if (!root) return;

  if (workoutTimer && (workoutTimer.userId !== currentUser?.id
    || workoutTimer.roomId !== (getClubIdFromUrl() ? currentClub?.room?.id : getRoomIdFromUrl())
    || getSecondaryView(window.location.search) || getInviteTokenFromUrl())) {
    resetWorkoutTimer();
  }

  if (currentUser) {
    unmountSignIn?.();
    unmountSignIn = null;
    mountedSignInElement = null;
  }

  if (currentUser) {
    if (!userProfile || userProfile.userId !== currentUser.id) {
      root.innerHTML = '<main class="screen"><section class="card profile-setup"><p role="status">Loading your player profile…</p></section></main>';
      try {
        const profile = await apiGetProfile();
        if (!isCurrentRender()) return;
        userProfile = profile;
      } catch (error) {
        if (!isCurrentRender()) return;
        root.innerHTML = `<main class="screen"><section class="card profile-setup">
          <p role="alert">${escapeSignInText(error instanceof Error ? error.message : 'Could not load your profile.')}</p>
          <button id="profile-retry" class="btn" type="button">Try again</button>
          <button id="profile-signout" class="btn btn-secondary" type="button">Sign Out</button>
        </section></main>`;
        root.querySelector('#profile-retry')?.addEventListener('click', () => void renderPage());
        root.querySelector('#profile-signout')?.addEventListener('click', () => void signOutFromApp());
        return;
      }
    }
    if (!userProfile.usernameSetupCompleted) {
      stopPolling();
      root.innerHTML = `<main class="screen" data-screen="username-setup"><section class="card profile-setup" aria-labelledby="username-title">
        ${renderUsernameForm(userProfile, true)}
        <button id="profile-signout" class="btn-logout" type="button">Sign Out</button>
      </section></main>`;
      mountUsernameForm(root, true, async (update) => {
        if (await saveProfile(update)) await renderPage();
      }, () => undefined);
      root.querySelector('#profile-signout')?.addEventListener('click', () => void signOutFromApp());
      return;
    }
  }

  const inviteToken = getInviteTokenFromUrl();
  if (inviteToken) {
    stopPolling();
    currentView = 'lobby';
    currentRoom = null;
    root.innerHTML = renderInviteScreen({ email: currentUser?.email, status: 'checking' });
    attachInviteHomeHandler(root);
    try {
      const resolution = await apiResolveRoomInvite(inviteToken);
      if (!isCurrentRender() || getInviteTokenFromUrl() !== inviteToken) return;
      if (resolution.status === 'member') {
        clearInviteTokenFromUrl();
        setRoomIdInUrl(resolution.roomId, 'lobby');
        await renderPage();
        return;
      }
      if (resolution.status === 'club-member') {
        clearInviteTokenFromUrl();
        setClubIdInUrl(resolution.clubId);
        await renderPage();
        return;
      }
      root.innerHTML = renderInviteScreen({ email: currentUser?.email, ...resolution });
      attachInviteHomeHandler(root);
      if (currentUser) attachInviteHandlers(root);
      else attachLoginHandlers(root);
    } catch {
      if (!isCurrentRender() || getInviteTokenFromUrl() !== inviteToken) return;
      root.innerHTML = renderInviteScreen({ email: currentUser?.email, status: 'error' });
      attachInviteHomeHandler(root);
      root.querySelector('#invite-retry-btn')?.addEventListener('click', () => void renderPage());
    }
    return;
  }

  if (!currentUser) {
    stopPolling();
    currentView = 'lobby';
    const publicHome = root.firstElementChild;
    if (
      publicHome?.getAttribute('data-screen') !== 'public-home'
      || publicHome.getAttribute('data-auth-state') !== 'signed-out'
    ) {
      root.innerHTML = renderPublicHome();
    }
    attachLoginHandlers(root);
    return;
  }

  refreshNotificationDeliveryState();

  if (!userProgress || userProgress.userId !== currentUser.id) {
    const progress = await apiGetProgress();
    if (!isCurrentRender()) return;
    userProgress = progress;
  }

  const secondaryView = getSecondaryView(window.location.search);
  if (secondaryView) {
    stopPolling();
    currentView = secondaryView;
    if (secondaryView === 'gear') {
      root.innerHTML = renderGearScreen();
      attachGearHandlers(root);
    } else if (secondaryView === 'shop') {
      root.innerHTML = renderGearScreen('shop');
      attachShopHandlers(root);
    } else {
      root.innerHTML = renderTalentTreeScreen();
      attachTalentTreeHandlers(root);
    }
    return;
  }

  const requestedPrimaryView = getPrimaryViewFromUrl();
  await loadFriends();
  if (!isCurrentRender()) return;
  const clubId = getClubIdFromUrl();
  if (clubId) {
    if (currentClub?.id !== clubId) {
      try {
        const club = await apiClub(`/api/clubs/${encodeURIComponent(clubId)}`);
        if (!isCurrentRender()) return;
        applyClub(club);
        await loadInvitesForCurrentRoom();
        if (!isCurrentRender()) return;
      } catch (error) {
        if (!isCurrentRender()) return;
        root.innerHTML = `<main class="screen"><section class="card profile-setup"><p role="alert">${escapeSignInText(error instanceof Error ? error.message : 'Could not open club.')}</p><a class="btn" href="/">Back to Home</a><button id="club-retry" class="btn-secondary" type="button">Try again</button></section></main>`;
        root.querySelector('#club-retry')?.addEventListener('click', () => void renderPage());
        return;
      }
    }
    currentView = 'workout';
    workoutPageOrigin = 'lobby';
    startPolling();
    if (!currentRoom) {
      root.innerHTML = renderPendingClubScreen(currentClub!);
      attachWorkoutHandlers(root);
      return;
    }
  }
  if (!currentRoom) currentView = requestedPrimaryView;

  if (!currentRoom) {
    const roomId = getRoomIdFromUrl();
    if (roomId) {
      try {
        workoutPageOrigin = requestedPrimaryView;
        const room = await apiGetRoom(roomId);
        if (!isCurrentRender()) return;
        currentRoom = room;
        rewardModalShown = false;
        await loadInvitesForCurrentRoom();
        if (!isCurrentRender()) return;
        startPolling();
      } catch {
        if (!isCurrentRender()) return;
        clearRoomIdInUrl();
        currentView = requestedPrimaryView;
      }
    }
  }

  if (!currentRoom) {
    await Promise.all([loadJoinedRoomsForUser(), ...(currentView === 'history' ? [] : [loadJoinedClubs()])]);
    if (!isCurrentRender()) return;
    if (currentView === 'history') {
      root.innerHTML = renderWorkoutHistoryScreen();
      attachWorkoutHistoryHandlers(root);
    } else {
      currentView = 'lobby';
      root.innerHTML = renderLobbyScreen();
      attachLobbyHandlers(root);
    }
    return;
  }

  currentView = 'workout';
  if (!workoutTimer || workoutTimer.roomId !== currentRoom.id) {
    resetWorkoutTimer();
    workoutTimer = { userId: currentUser.id, roomId: currentRoom.id, widget: new WorkoutTimerWidget() };
  }
  workoutTimer.widget.prepareForRender();
  root.innerHTML = renderWorkoutScreen();
  workoutTimer.widget.mount(root.querySelector<HTMLElement>('[data-workout-timer]')!);
  attachWorkoutHandlers(root);
  showCompletionModalIfNeeded();
}

document.addEventListener('DOMContentLoaded', async () => {
  const slowAuthTimer = window.setTimeout(() => {
    const message = document.getElementById('auth-session-message');
    if (message) message.textContent = AUTH_CHECK_SLOW_MESSAGE;
  }, AUTH_CHECK_SLOW_MS);

  const { data } = await supabase.auth.getSession();
  window.clearTimeout(slowAuthTimer);
  const sessionUser = data?.session?.user;

  currentUser = sessionUser
    ? { id: sessionUser.id, email: sessionUser.email }
    : null;

  if (currentUser) {
    emailSignIn.clear();
    clearLegacyLocalProgress(currentUser.id);
    refreshNotificationDeliveryState();
    void reconcileNotificationDelivery();
  }

  supabase.auth.onAuthStateChange((event, session) => {
    const previousUserId = currentUser?.id;
    const authUser = session?.user;
    currentUser = authUser
      ? { id: authUser.id, email: authUser.email }
      : null;

    // Supabase also confirms SIGNED_IN on tab refocus and refreshes tokens in
    // the background. Keep the current page and in-flight work for that user.
    if (currentUser?.id === previousUserId && event !== 'SIGNED_OUT') return;

    profileSessionVersion += 1;
    resetWorkoutTimer();
    userProfile = null;
    closeUsernameDialog();
    if (currentUser || event === 'SIGNED_OUT') emailSignIn.clear();

    currentRoom = null;
    currentClub = null;
    joinedClubs = [];
    clubsError = '';
    creationMode = 'workout';
    clubNotice = '';
    ++clubRequestVersion;
    userProgress = null;
    joinedRooms = [];
    friends = [];
    friendsError = '';
    ++friendsRequestVersion;
    pendingFriends = new Set();
    roomInvites = [];
    latestCreatedInvite = null;
    selectedInviteExpiration = DEFAULT_INVITE_EXPIRATION;
    selectedInviteFriendUserId = '';
    latestInviteFriendName = '';
    pendingActivitySubmission = null;
    currentView = getPrimaryViewFromUrl();
    gearPage = null;
    shopPage = null;
    if (shopPurchases?.userId !== currentUser?.id) shopPurchases = null;
    workoutPageOrigin = getPrimaryViewFromUrl();
    rewardModalShown = false;
    stopPolling();
    if (currentUser) {
      clearLegacyLocalProgress(currentUser.id);
      refreshNotificationDeliveryState();
      void reconcileNotificationDelivery();
    } else {
      resetNotificationDeliveryClient();
    }
    setTimeout(() => void renderPage(), 0);
  });

  await renderPage();
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const shopLink = target.closest('[data-open-shop]');
  const gearLink = target.closest('[data-open-gear]');
  if (currentUser && (target.closest('#hero-gold-btn') || ((shopLink || gearLink) && shouldHandleNavigationClick(event)))) {
    event.preventDefault();
    void openSecondaryPage(gearLink ? 'gear' : 'shop');
    return;
  }

  if (target.closest('#hero-level-btn') && currentUser) {
    void openSecondaryPage('gear');
    return;
  }

  const homeLink = target.closest<HTMLAnchorElement>('.hero-logo-link');
  if (!homeLink || !shouldHandleNavigationClick(event)) return;

  event.preventDefault();
  void navigateHome();
});

window.addEventListener('popstate', () => {
  resetCurrentRoomState();
  const requestedView = getPrimaryViewFromUrl();
  currentView = requestedView;
  workoutPageOrigin = requestedView;
  void renderPage();
});

document.addEventListener('visibilitychange', () => void handleVisibilityChange());

window.addEventListener('pagehide', resetWorkoutTimer);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) void renderPage();
});

MOBILE_COLLAPSIBLE_MEDIA.addEventListener('change', () => {
  const root = document.getElementById('app');
  if (root) syncLobbyCollapsibleCardControls(root);
});
